import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AuthMeResponse,
  EvalNotes,
  EvalOverviewDTO,
  EvalQuarter,
  EvalSlot,
  EvaluationDTO,
  FollowupItem,
  MyEvaluationDTO,
  ObjectiveCheckStatus,
  ObjectiveDTO,
  PayrollLineStatus,
  PaymentMethod,
  PayrollMonthDTO,
  PayrollGridDTO,
  PayrollNoteDTO,
  PayrollStage,
  PayrollTag,
  NotificationListResponse,
  SalaryHistoryEntryDTO,
} from '@pointage/shared';
import type { Permission, RecBar, RecDimScore, RecGridCrit, RecRef, RecSettings, Role, UsageArea } from '@pointage/shared';
import { apiClient, AuthRequiredError } from './client.js';
import { getSubscription, subscribePush, unsubscribePush } from '../lib/push.js';

// ─── /api/auth/me ────────────────────────────────────────────

export function useMe() {
  return useQuery<AuthMeResponse | null>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await apiClient.get('/auth/me');
      if (res.status === 401) {
        throw new AuthRequiredError();
      }
      if (res.status >= 400) {
        throw new Error(`/auth/me failed: ${res.status}`);
      }
      return res.data as AuthMeResponse;
    },
    retry: false,
    staleTime: 60_000,
  });
}

// ─── « Voir comme » (impersonation admin) ────────────────────
// Les deux mutations vident TOUT le cache : aucune donnée chargée
// sous une identité ne doit survivre au changement d'identité.

export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.post('/auth/impersonate', { userId });
      if (res.status >= 400) {
        const err = new Error(`POST /auth/impersonate failed: ${res.status}`);
        (err as Error & { code?: string }).code = res.data?.error;
        throw err;
      }
    },
    onSuccess: () => qc.clear(),
  });
}

export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/auth/impersonate');
    },
    onSuccess: () => qc.clear(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/logout');
    },
    onSuccess: () => {
      qc.clear();
    },
  });
}

// ─── /api/entities ───────────────────────────────────────────

export interface EntityDTO {
  id: string;
  name: string;
  short: string;
  color: string;
  email: string;
}

export function useEntities(enabled = true) {
  return useQuery<EntityDTO[]>({
    queryKey: ['entities'],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get('/entities');
      if (res.status >= 400) throw new Error(`/entities failed: ${res.status}`);
      return res.data as EntityDTO[];
    },
    staleTime: 5 * 60_000,
  });
}

// ─── /api/entities/companies (Paramètres → Sociétés) ─────────
// Identité légale par société : ce que les PDF générés estampillent.
// Séparé de useEntities() — que tous les écrans chargent — parce que le
// logo est un data-URL base64 qui n'a rien à faire dans ce payload.

export interface CompanyDTO {
  id: string;
  name: string;
  short: string;
  color: string;
  legalName: string;
  address: string;
  nif: string;
  nis: string;
  ai: string;
  cnas: string;
  rib: string;
  logo: string | null; // data-URL, directement utilisable en <img src>
  // Raison sociale réellement imprimée (saisie, sinon repli historique).
  effectiveLegalName: string;
}

export type CompanyPatch = Partial<
  Pick<CompanyDTO, 'legalName' | 'address' | 'nif' | 'nis' | 'ai' | 'cnas' | 'rib'>
>;

export function useCompanies() {
  return useQuery<CompanyDTO[]>({
    queryKey: ['companies'],
    queryFn: async () => {
      const res = await apiClient.get('/entities/companies');
      if (res.status >= 400) throw new Error(`/entities/companies failed: ${res.status}`);
      return res.data as CompanyDTO[];
    },
    staleTime: 60_000,
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: CompanyPatch }) => {
      const res = await apiClient.put(`/entities/${args.id}/company`, args.patch);
      if (res.status >= 400) {
        const err = new Error(`PUT /entities/${args.id}/company failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as CompanyDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

// Le logo passe en multipart (PNG/JPEG, 512 Ko max) plutôt qu'en JSON :
// le corps JSON de l'app est plafonné à 1 Mo, base64 inflate de 33 %.
export function useUploadCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', args.file);
      const res = await apiClient.post(`/entities/${args.id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 400) {
        const err = new Error(`POST /entities/${args.id}/logo failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as CompanyDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useDeleteCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/entities/${id}/logo`);
      if (res.status >= 400) throw new Error(`DELETE /entities/${id}/logo failed: ${res.status}`);
      return res.data as CompanyDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

// ─── /api/attendance (daily roster) ──────────────────────────

export type DecisionType = 'PRESENT' | 'TT' | 'CONGE' | 'MALADIE' | 'RDV_PERSO' | 'RECUP' | 'AUTRE';
export type AutoDecision = 'PRESENT' | 'CONGE' | 'MALADIE' | null;
export type NotifiedInAdvance = 'YES' | 'NO';
// Two signatures: the preparer's (VALIDATED_L1) then the reviewer's
// (VALIDATED). READY = nothing blocking, nothing signed yet.
export type ValidationStage = 'DRAFT' | 'READY' | 'VALIDATED_L1' | 'VALIDATED';

export interface AttendanceRecordDTO {
  id: string;
  employeeId: string;
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  checkInManual: boolean;
  checkOutManual: boolean;
  /** What the pointeuse recorded — differs from checkIn/checkOut only
   *  after a hand correction (null = no device punch on that side). */
  checkInDevice: string | null;
  checkOutDevice: string | null;
  status: string;
  note: string | null;
  source: string | null;
  lastEditedBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Daily-entry workflow (Phase A.2)
  decisionType: DecisionType | null;
  decisionCustomCode: string | null;
  decisionSetBy: string | null;
  /** display name of the decision author (server-resolved, proto chip). */
  decisionSetByName: string | null;
  decisionSetAt: string | null;
  decisionNote: string | null;
  notifiedInAdvance: NotifiedInAdvance | null;
  notifiedDetail: string | null;
  notifiedSetBy: string | null;
  notifiedSetAt: string | null;
  validationStage: ValidationStage;
  /** first signature — who finished the saisie, and when. */
  validatedL1By: string | null;
  validatedL1ByName: string | null;
  validatedL1At: string | null;
  validatedBy: string | null;
  /** display name of whoever closed the day (server-resolved). */
  validatedByName: string | null;
  validatedAt: string | null;
}

export interface DailyTagDTO {
  type: 'ok' | 'late' | 'more' | 'less' | 'abs' | 'tt' | 'justified';
  label: string;
  delta?: number;
}

export interface RecordStatusDTO {
  code: string;
  isHoliday: boolean;
  isWorkDay: boolean;
  hasRecord: boolean;
  status: string | null;
  durationMinutes: number | null;
  isLate: boolean;
  isShort: boolean;
  isMissed: boolean;
  isNoRecord: boolean;
  isAbsentMarked: boolean;
  isIncomplete: boolean;
}

export interface DailyRosterEntry {
  employee: { id: string; code: string; name: string; entityId: string; role: string | null; email: string | null };
  record: AttendanceRecordDTO | null;
  dayCode: string;
  recordStatus: RecordStatusDTO;
  tags: DailyTagDTO[];
  // §9.2: entity-resolved expected schedule for the hero timeline
  // (08:00 / workHours fallback baked server-side). Optional so older
  // fixtures/payloads still typecheck; the timeline falls back gracefully.
  expectedStart?: string; // 'HH:MM'
  expectedHours?: number; // decimal hours
  notifiedRequired: boolean;
  // Phase E.1.2 derived fields (auto-decisions are computed, not stored).
  autoDecision: AutoDecision;
  effectiveDecision: DecisionType | null;
  locked: boolean;
  needsDecision: boolean;
  needsNotified: boolean;
}

export function useDailyRoster(opts: {
  date: string;
  entityId: string;
  pollMs?: number;
  enabled?: boolean;
}) {
  return useQuery<DailyRosterEntry[]>({
    queryKey: ['attendance', 'daily', opts.date, opts.entityId],
    queryFn: async () => {
      const params: Record<string, string> = { date: opts.date };
      if (opts.entityId && opts.entityId !== 'all') params.entityId = opts.entityId;
      const res = await apiClient.get('/attendance', { params });
      if (res.status >= 400) throw new Error(`/attendance failed: ${res.status}`);
      return res.data as DailyRosterEntry[];
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.pollMs ?? false,
    staleTime: 30_000,
  });
}

// ─── /api/attendance/coverage (Saisie date-nav day states) ───
export interface AttendanceCoverageDay {
  date: string;
  records: number;
  punches: number; // records with at least one real punch (pointeuse data)
  validated: boolean;
  validatedRecords: number; // records already in validationStage VALIDATED
  /** signed once, waiting for the final validation */
  prepared: boolean;
}
export function useAttendanceCoverage(opts: { from: string; to: string; entityId: string; enabled?: boolean }) {
  return useQuery<{ from: string; to: string; days: AttendanceCoverageDay[] }>({
    queryKey: ['attendance', 'coverage', opts.from, opts.to, opts.entityId],
    queryFn: async () => {
      const params: Record<string, string> = { from: opts.from, to: opts.to };
      if (opts.entityId && opts.entityId !== 'all') params.entityId = opts.entityId;
      const res = await apiClient.get('/attendance/coverage', { params });
      if (res.status >= 400) throw new Error(`/attendance/coverage failed: ${res.status}`);
      return res.data as { from: string; to: string; days: AttendanceCoverageDay[] };
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

// ─── /api/attendance PUT (single upsert) ─────────────────────

export interface AttendanceUpdate {
  checkIn?: string | null;
  checkOut?: string | null;
  status?: string;
  note?: string | null;
}

export function usePutAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { date: string; employeeId: string; update: AttendanceUpdate }) => {
      const res = await apiClient.put(
        `/attendance/${args.date}/${args.employeeId}`,
        args.update,
      );
      if (res.status >= 400) {
        const err = new Error(`PUT /attendance failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as AttendanceRecordDTO;
    },
    onSuccess: (_data, vars) => {
      // Invalidate the day's roster for both "all" and the specific
      // entity, since either filter could be active in the UI.
      qc.invalidateQueries({ queryKey: ['attendance', 'daily', vars.date] });
      qc.invalidateQueries({ queryKey: ['followups', 'daily', vars.date] });
    },
  });
}

// ─── /api/attendance daily-entry workflow (Phase A.2) ────────

function invalidateDay(qc: ReturnType<typeof useQueryClient>, date: string) {
  qc.invalidateQueries({ queryKey: ['attendance', 'daily', date] });
  qc.invalidateQueries({ queryKey: ['attendance', 'validation'] });
  qc.invalidateQueries({ queryKey: ['followups', 'daily', date] });
}

export function usePatchDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      recordId: string;
      date: string;
      decisionType: DecisionType;
      decisionNote?: string | null;
      customCode?: string | null;
    }) => {
      const res = await apiClient.patch(`/attendance/${args.recordId}/decision`, {
        decisionType: args.decisionType,
        ...(args.decisionNote !== undefined ? { decisionNote: args.decisionNote } : {}),
        ...(args.customCode !== undefined ? { customCode: args.customCode } : {}),
      });
      if (res.status >= 400) throw new Error(`PATCH decision failed: ${res.status}`);
      return res.data as AttendanceRecordDTO;
    },
    onSuccess: (_d, vars) => invalidateDay(qc, vars.date),
  });
}

export function usePatchNotified() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      recordId: string;
      date: string;
      notifiedInAdvance: NotifiedInAdvance | null;
      notifiedDetail?: string | null;
    }) => {
      const res = await apiClient.patch(`/attendance/${args.recordId}/notified`, {
        notifiedInAdvance: args.notifiedInAdvance,
        ...(args.notifiedDetail !== undefined ? { notifiedDetail: args.notifiedDetail } : {}),
      });
      if (res.status >= 400) throw new Error(`PATCH notified failed: ${res.status}`);
      return res.data as AttendanceRecordDTO;
    },
    onSuccess: (_d, vars) => invalidateDay(qc, vars.date),
  });
}

export interface ValidationBlocker {
  employeeId: string;
  missing: Array<'decision' | 'notified'>;
}
export interface ValidationStatusDTO {
  stage: ValidationStage;
  blockers: ValidationBlocker[];
  /** names + timestamps of the two signatures, when they exist. */
  preparedBy: string | null;
  preparedAt: string | null;
  validatedBy: string | null;
  validatedAt: string | null;
}

export function useValidationStatus(opts: { date: string; entityId: string; enabled?: boolean }) {
  return useQuery<ValidationStatusDTO>({
    queryKey: ['attendance', 'validation', opts.entityId, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/attendance/validation-status', {
        params: { entityId: opts.entityId, date: opts.date },
      });
      if (res.status >= 400) throw new Error(`validation-status failed: ${res.status}`);
      return res.data as ValidationStatusDTO;
    },
    // Only meaningful for a single entity (the server rejects 'all').
    enabled: (opts.enabled ?? true) && opts.entityId !== 'all',
    staleTime: 15_000,
  });
}

// Première signature: « la saisie est finie ». Aucun mouvement de solde,
// aucun digest — la journée part en revue.
export function useValidateDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { entityId: string; date: string }) => {
      const res = await apiClient.post('/attendance/validate-day', args);
      if (res.status >= 400) throw dayWorkflowError('validate-day', res);
      return res.data as { stage: ValidationStage; prepared: number };
    },
    onSuccess: (_d, vars) => invalidateDay(qc, vars.date),
  });
}

// Seconde signature: la revue clôture la journée. C'est ICI que le
// ledger congés bouge et que le récapitulatif quotidien part.
export function useValidateDayFinal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { entityId: string; date: string }) => {
      const res = await apiClient.post('/attendance/validate-day/final', args);
      if (res.status >= 400) throw dayWorkflowError('validate-day/final', res);
      // negativeBalances: la journée EST validée; ces soldes sont juste
      // passés sous zéro (crédit RH manquant), à signaler sans bloquer.
      return res.data as {
        stage: ValidationStage;
        validated: number;
        negativeBalances: Array<{ name: string; type: LeaveType; balanceAfter: number }>;
      };
    },
    onSuccess: (_d, vars) => {
      invalidateDay(qc, vars.date);
      // Le débit vient de bouger un solde: la vue d'ensemble Congés et
      // les soldes affichés ne sont plus à jour.
      void qc.invalidateQueries({ queryKey: ['leaves-overview'] });
      void qc.invalidateQueries({ queryKey: ['leave-balances'] });
    },
  });
}

// Rouvre une journée clôturée: elle repasse en revue et les débits de
// congés écrits par la validation sont contre-passés.
export function useReopenDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { entityId: string; date: string; reason?: string }) => {
      const res = await apiClient.post('/attendance/reopen-day', args);
      if (res.status >= 400) throw dayWorkflowError('reopen-day', res);
      return res.data as { stage: ValidationStage; reopened: number; reversedLeaveDebits: number };
    },
    onSuccess: (_d, vars) => {
      invalidateDay(qc, vars.date);
      void qc.invalidateQueries({ queryKey: ['leaves-overview'] });
      void qc.invalidateQueries({ queryKey: ['leave-balances'] });
      void qc.invalidateQueries({ queryKey: ['attendance', 'day-changes'] });
    },
  });
}

function dayWorkflowError(what: string, res: { status: number; data: unknown }): Error {
  const err = new Error(`${what} failed: ${res.status}`);
  (err as Error & { body?: unknown; status?: number }).body = res.data;
  (err as Error & { body?: unknown; status?: number }).status = res.status;
  return err;
}

// ─── /api/attendance/day-changes ─────────────────────────────
// Ce qui a bougé sur la journée depuis la première signature — l'audit
// existe déjà, on le ramène cadré sur cette entité-date.
export interface DayChangeDTO {
  id: string;
  action: string;
  at: string;
  by: string | null;
  employeeName: string | null;
  payload: Record<string, unknown> | null;
}

export function useDayChanges(opts: { date: string; entityId: string; enabled?: boolean }) {
  return useQuery<{ since: string | null; changes: DayChangeDTO[] }>({
    queryKey: ['attendance', 'day-changes', opts.entityId, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/attendance/day-changes', {
        params: { entityId: opts.entityId, date: opts.date },
      });
      if (res.status >= 400) throw new Error(`day-changes failed: ${res.status}`);
      return res.data as { since: string | null; changes: DayChangeDTO[] };
    },
    enabled: (opts.enabled ?? true) && opts.entityId !== 'all',
    staleTime: 15_000,
  });
}

// ─── /api/monthly-grid (Phase B.1) ───────────────────────────

export type MonthCellType =
  | 'p' | 'r' | 'recup' | 'tt' | 'c' | 'm' | 'aj' | 'anj' | 'h' | 'we' | 'empty' | 'need'
  // 'na' = pointage non importé ce jour-là pour TOUTE l'entité: le
  // fichier manque, la personne n'est pas en cause.
  | 'na';
export type MonthlyStage =
  | 'DRAFT' | 'PREPARED_BY_LAMIS' | 'REVIEWED_BY_BOUCHRA' | 'VALIDATED_BY_CHOUAIB';

export type GmNeedKind = 'court' | 'absj' | 'double';

export interface MonthCellDTO {
  date: string;
  typeReal: MonthCellType;
  typePaie: MonthCellType;
  hoursReal: number | null;
  hoursPaie: number | null;
  decisionType: DecisionType | null;
  isWeekend: boolean;
  isFerie: boolean;
  /** Why the paie cell queues for arbitration (kept once resolved). */
  needKind: GmNeedKind | null;
  checkIn: string | null;
  checkOut: string | null;
  /** Committed paie classification, when one resolved this cell. */
  paieDecision: { key: string; label: string } | null;
}
export interface MonthGridRowDTO {
  employee: { id: string; code: string; name: string; entityId: string; role: string | null };
  cells: MonthCellDTO[];
  totals: {
    present: number; retard: number; absent: number; hoursReal: number; hoursPaie: number;
    needsCount: number; missingCount: number;
  };
}
export interface MonthGridDTO {
  entity: { id: string; name: string; short: string };
  year: number;
  month: number;
  /** Resolved expected day-hours (entity override → global setting). */
  expectedHours: number;
  days: Array<{
    date: string; day: number; dow: number; isWeekend: boolean; isFerie: boolean; isToday: boolean;
    /** Aucun pointage pour l'entité ce jour-là → import manquant. */
    pointageMissing: boolean;
  }>;
  /** Les dates `pointageMissing`, dans l'ordre. */
  missingPointageDates: string[];
  submission: {
    stage: MonthlyStage;
    preparedBy: string | null; preparedAt: string | null;
    reviewedBy: string | null; reviewedAt: string | null;
    validatedBy: string | null; validatedAt: string | null;
  };
  rows: MonthGridRowDTO[];
}

export function useMonthlyGrid(opts: { entityId: string; year: number; month: number; enabled?: boolean }) {
  return useQuery<MonthGridDTO>({
    queryKey: ['monthly-grid', opts.entityId, opts.year, opts.month],
    queryFn: async () => {
      const res = await apiClient.get('/monthly-grid', {
        params: { entityId: opts.entityId, year: opts.year, month: opts.month },
      });
      if (res.status >= 400) throw new Error(`monthly-grid failed: ${res.status}`);
      return res.data as MonthGridDTO;
    },
    enabled: (opts.enabled ?? true) && opts.entityId !== 'all',
    staleTime: 30_000,
  });
}

export function useSubmitMonthly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      entityId: string; year: number; month: number; stage: MonthlyStage;
      /** Passer outre l'avertissement « pointage non importé ». */
      force?: boolean;
    }) => {
      const res = await apiClient.post('/monthly-grid/submit', args);
      if (res.status >= 400) {
        const err = new Error(`submit failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { stage: MonthlyStage };
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['monthly-grid', v.entityId, v.year, v.month] }),
  });
}

export function usePaieAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      entityId: string; year: number; month: number; employeeId: string; date: string; manualHours: number; reason?: string;
    }) => {
      const res = await apiClient.patch('/monthly-grid/paie-adjustment', args);
      if (res.status >= 400) {
        const err = new Error(`paie-adjustment failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { key: string; manualHours: number; reason: string };
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['monthly-grid', v.entityId, v.year, v.month] }),
  });
}

// Commits (or clears, key=null) a paie classification for a queued
// cell — touches the paie version ONLY, réelle stays intact.
export function usePaieDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      entityId: string; year: number; month: number; employeeId: string; date: string; key: string | null; note?: string;
    }) => {
      const res = await apiClient.patch('/monthly-grid/paie-decision', args);
      if (res.status >= 400) {
        const err = new Error(`paie-decision failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { key: string | null; label?: string; result?: string };
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['monthly-grid', v.entityId, v.year, v.month] }),
  });
}

// ─── /api/employees (list, for pickers) ─────────────────────
export interface EmployeeDTO {
  id: string;
  code: string;
  name: string;
  entityId: string;
  department: string | null;
  email: string | null;
}
// Picker feed: current staff only — archived fiches and anyone past their
// date de sortie are not selectable targets for a new action.
export function useEmployees(enabled = true) {
  return useQuery<EmployeeDTO[]>({
    queryKey: ['employees', 'active'],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get('/employees', { params: { active: 'true' } });
      if (res.status >= 400) throw new Error(`/employees failed: ${res.status}`);
      return res.data as EmployeeDTO[];
    },
    staleTime: 5 * 60_000,
  });
}

// ─── /api/employees (full records — Phase D Employees page) ──
export interface EmployeeFullDTO {
  id: string;
  code: string;
  name: string;
  entityId: string;
  department: string | null;
  email: string | null;
  phone: string | null;
  congeBalance: number;
  recupBalance: number;
  /** null = le compte n'a pas payroll.view (le serveur ne l'envoie pas). */
  salary: number | null;
  active: boolean;
  hiredAt: string | null;
  dateSortie: string | null;
  probationStart: string | null;
  probationMonths: number | null;
  probationRenewMonths: number | null;
  probationEnd: string | null;
  probationOutcome: 'CONFIRMED' | 'RUPTURE' | null;
  probationOutcomeAt: string | null;
  contractType: 'CDI' | 'CDD';
  ttDays: number[];
  payActualHours: boolean;
  statsExcluded: boolean;
  pointageExcluded: boolean;
  payrollExcluded: boolean;
  cddEnd: string | null;
  cddMotif: string | null;
  cddRenewals: number;
  cddOutcome: 'CDI' | 'NONRENOUVELE' | 'TERMINE' | null;
  cddOutcomeAt: string | null;
  notes: string | null;
  civility: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  cnasNumber: string | null;
  bankLabel: string | null;
  bankAccount: string | null;
  bankSwift: string | null;
  paymentMethod: PaymentMethod;
  emergency1Name: string | null;
  emergency1Phone: string | null;
  emergency1Relation: string | null;
  emergency2Name: string | null;
  emergency2Phone: string | null;
  emergency2Relation: string | null;
  balanceSource?: 'ledger' | 'field';
  createdAt: string;
  updatedAt: string;
}

export function useEmployeesFull() {
  return useQuery<EmployeeFullDTO[]>({
    queryKey: ['employees-full'],
    queryFn: async () => {
      const res = await apiClient.get('/employees');
      if (res.status >= 400) throw new Error(`/employees failed: ${res.status}`);
      return res.data as EmployeeFullDTO[];
    },
    staleTime: 60_000,
  });
}

export interface EmployeePatch {
  name?: string;
  entityId?: string;
  department?: string | null;
  email?: string | null;
  phone?: string | null;
  salary?: number;
  active?: boolean;
  hiredAt?: string | null;
  dateSortie?: string | null;
  probationStart?: string | null;
  probationMonths?: number | null;
  contractType?: 'CDI' | 'CDD';
  ttDays?: number[];
  payActualHours?: boolean;
  statsExcluded?: boolean;
  pointageExcluded?: boolean;
  payrollExcluded?: boolean;
  cddEnd?: string | null;
  cddMotif?: string | null;
  notes?: string | null;
  civility?: string | null;
  birthDate?: string | null;
  birthPlace?: string | null;
  cnasNumber?: string | null;
  bankLabel?: string | null;
  bankAccount?: string | null;
  bankSwift?: string | null;
  paymentMethod?: PaymentMethod;
  emergency1Name?: string | null;
  emergency1Phone?: string | null;
  emergency1Relation?: string | null;
  emergency2Name?: string | null;
  emergency2Phone?: string | null;
  emergency2Relation?: string | null;
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: EmployeePatch }) => {
      const res = await apiClient.patch(`/employees/${args.id}`, args.patch);
      if (res.status >= 400) {
        const err = new Error(`PATCH /employees failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as EmployeeFullDTO;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// Create employee. Starting balances are seeded server-side as
// INITIAL_STOCK ledger transactions (not direct field writes).
export interface EmployeeCreate {
  name: string;
  entityId: string;
  code?: string;
  department?: string | null;
  email?: string | null;
  phone?: string | null;
  salary?: number;
  hiredAt?: string | null;
  probationStart?: string | null;
  probationMonths?: number | null;
  contractType?: 'CDI' | 'CDD';
  ttDays?: number[];
  payActualHours?: boolean;
  cddEnd?: string | null;
  cddMotif?: string | null;
  notes?: string | null;
  congeBalance?: number;
  recupBalance?: number;
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EmployeeCreate) => {
      const res = await apiClient.post('/employees', body);
      if (res.status >= 400) {
        const err = new Error(`POST /employees failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as EmployeeFullDTO;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// Deactivate (soft delete → active=false). Reactivate is a normal
// PATCH { active: true } via useUpdateEmployee.
export function useDeactivateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/employees/${id}`);
      if (res.status >= 400) throw new Error(`DELETE /employees failed: ${res.status}`);
      return res.data as EmployeeFullDTO;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// Ledger-backed balance adjustment (MANUAL_ADDITION + required reason).
export function useAdjustEmployeeBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; type: LeaveType; amount: number; reason: string }) => {
      const res = await apiClient.post(`/employees/${args.id}/balance`, {
        type: args.type,
        amount: args.amount,
        reason: args.reason,
      });
      if (res.status >= 400) {
        const err = new Error(`POST /employees/:id/balance failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { type: LeaveType; amount: number; current: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      qc.invalidateQueries({ queryKey: ['leave-balances'] });
      qc.invalidateQueries({ queryKey: ['leave-tx'] });
      qc.invalidateQueries({ queryKey: ['leave-detail'] });
    },
  });
}

// ─── /api/postes (Parameters catalog + employee poste history) ──
export interface PosteDTO {
  id: string;
  label: string;
  entityId: string | null;
  active: boolean;
  createdAt: string;
}

export function usePostes(opts?: { entityId?: string; activeOnly?: boolean }) {
  return useQuery<PosteDTO[]>({
    queryKey: ['postes', opts?.entityId ?? 'all', opts?.activeOnly ?? false],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (opts?.entityId && opts.entityId !== 'all') params.entityId = opts.entityId;
      if (opts?.activeOnly) params.active = 'true';
      const res = await apiClient.get('/postes', { params });
      if (res.status >= 400) throw new Error(`/postes failed: ${res.status}`);
      return res.data as PosteDTO[];
    },
    staleTime: 5 * 60_000,
  });
}

// `entityIds` est une portée MULTIPLE : [] = toutes les entités (une
// ligne à entityId null), sinon une ligne de catalogue par entité.
// La réponse est donc un tableau.
export function useCreatePoste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { label: string; entityIds?: string[] }) => {
      const res = await apiClient.post('/postes', body);
      if (res.status >= 400) {
        const err = new Error(`POST /postes failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as PosteDTO[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['postes'] }),
  });
}

// L'id désigne le GROUPE (toutes les lignes du même libellé) : le
// renommage les touche toutes, `entityIds` ajoute/retire des lignes.
// Réponse : les lignes actives du groupe après édition.
export function useUpdatePoste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: { label?: string; entityIds?: string[]; active?: boolean } }) => {
      const res = await apiClient.patch(`/postes/${args.id}`, args.patch);
      if (res.status >= 400) {
        const err = new Error(`PATCH /postes failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as PosteDTO[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['postes'] }),
  });
}

export function useDeletePoste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/postes/${id}`);
      if (res.status >= 400) throw new Error(`DELETE /postes failed: ${res.status}`);
      return res.data as PosteDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['postes'] }),
  });
}

export interface EmployeePosteDTO {
  id: string;
  posteId: string;
  label: string;
  dateDebut: string;
  current?: boolean;
}

export function useEmployeePostes(employeeId: string | null) {
  return useQuery<EmployeePosteDTO[]>({
    queryKey: ['employee-postes', employeeId],
    queryFn: async () => {
      const res = await apiClient.get(`/employees/${employeeId}/postes`);
      if (res.status >= 400) throw new Error(`employee postes failed: ${res.status}`);
      return res.data as EmployeePosteDTO[];
    },
    enabled: !!employeeId,
    staleTime: 60_000,
  });
}

export function useAddEmployeePoste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; posteId: string; dateDebut: string }) => {
      const res = await apiClient.post(`/employees/${args.id}/postes`, {
        posteId: args.posteId,
        dateDebut: args.dateDebut,
      });
      if (res.status >= 400) {
        const err = new Error(`add poste failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as EmployeePosteDTO;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['employee-postes', v.id] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
  });
}

// ─── /api/holidays CRUD (Parameters · jours fériés) ──────────
export interface HolidayDTO {
  id: string;
  date: string;
  name: string;
  entityId: string | null;
  createdAt?: string;
}

export function useHolidaysList(opts?: { year?: number; entityId?: string }) {
  return useQuery<HolidayDTO[]>({
    queryKey: ['holidays-list', opts?.year ?? 'all', opts?.entityId ?? 'all'],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (opts?.year) params.year = opts.year;
      if (opts?.entityId && opts.entityId !== 'all') params.entityId = opts.entityId;
      const res = await apiClient.get('/holidays', { params });
      if (res.status >= 400) throw new Error(`/holidays failed: ${res.status}`);
      return res.data as HolidayDTO[];
    },
    staleTime: 5 * 60_000,
  });
}

function invalidateHolidays(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['holidays-list'] });
  qc.invalidateQueries({ queryKey: ['holidays'] });
}

// `entityIds` est une portée MULTIPLE : [] = toutes les entités (une
// ligne à entityId null), sinon une ligne par entité, créées ensemble.
// La réponse est donc un tableau.
export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { date: string; name: string; entityIds?: string[] }) => {
      const res = await apiClient.post('/holidays', body);
      if (res.status >= 400) {
        const err = new Error(`POST /holidays failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as HolidayDTO[];
    },
    onSuccess: () => invalidateHolidays(qc),
  });
}

// Même logique de groupe que les postes : l'id identifie le jour férié
// (toutes ses lignes d'entité), qui se déplace/renomme d'un bloc.
export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: { date?: string; name?: string; entityIds?: string[] } }) => {
      const res = await apiClient.patch(`/holidays/${args.id}`, args.patch);
      if (res.status >= 400) {
        const err = new Error(`PATCH /holidays failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as HolidayDTO[];
    },
    onSuccess: () => invalidateHolidays(qc),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/holidays/${id}`);
      if (res.status >= 400) throw new Error(`DELETE /holidays failed: ${res.status}`);
      return res.data as HolidayDTO;
    },
    onSuccess: () => invalidateHolidays(qc),
  });
}

// ─── /api/users (Accès & utilisateurs + Activity actor filter) ──
export interface UserDTO {
  id: string;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  employeeId: string | null;
  permsGranted: string[];
  permsRevoked: string[];
  entityScope: string[];
  permissions: Permission[];
}

export function useUsers(enabled = true) {
  return useQuery<UserDTO[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await apiClient.get('/users');
      if (res.status >= 400) throw new Error(`/users failed: ${res.status}`);
      return res.data as UserDTO[];
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export interface InviteUserInput {
  email: string;
  name?: string;
  role: Role;
  entityScope?: string[];
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteUserInput) => {
      const res = await apiClient.post('/users', input);
      if (res.status >= 400) {
        const err = new Error(`POST /users failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as UserDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export interface UpdateUserInput {
  id: string;
  role?: Role;
  active?: boolean;
  permsGranted?: string[];
  permsRevoked?: string[];
  entityScope?: string[];
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateUserInput) => {
      const res = await apiClient.patch(`/users/${id}`, patch);
      if (res.status >= 400) {
        const err = new Error(`PATCH /users/${id} failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as UserDTO;
    },
    onSuccess: (data) => {
      qc.setQueryData<UserDTO[]>(['users'], (old) =>
        old ? old.map((u) => (u.id === data.id ? data : u)) : old,
      );
      // Role/permission edits can affect the signed-in session view.
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ─── /api/settings (Phase D Settings page) ──────────────────
export type SettingsMap = Record<string, unknown>;

export function useSettings(enabled = true) {
  return useQuery<SettingsMap>({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await apiClient.get('/settings');
      if (res.status >= 400) throw new Error(`/settings failed: ${res.status}`);
      return res.data as SettingsMap;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SettingsMap) => {
      const res = await apiClient.put('/settings', patch);
      if (res.status >= 400) {
        const err = new Error(`PUT /settings failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as SettingsMap;
    },
    onSuccess: (data) => {
      qc.setQueryData(['settings'], data);
      // Module flags travel through /api/auth/me (sidebar + route guards).
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ─── /api/activity (Phase D Activity page — full log) ───────
export interface ActivityLogEntryDTO {
  id: string;
  userId: string | null;
  user: { id: string; email: string; name: string } | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  /** Noms résolus serveur (l'employé / l'entité que la ligne concerne). */
  subjectName: string | null;
  entityName: string | null;
}
export interface ActivityLogPage {
  total: number;
  limit: number;
  offset: number;
  entries: ActivityLogEntryDTO[];
}
export interface ActivityFilters {
  userId?: string;
  action?: string;
  entityId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export function useActivityLog(filters: ActivityFilters, enabled = true) {
  return useQuery<ActivityLogPage>({
    queryKey: ['activity-log', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit: filters.limit ?? 200 };
      if (filters.userId) params.userId = filters.userId;
      if (filters.action) params.action = filters.action;
      if (filters.entityId) params.entityId = filters.entityId;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await apiClient.get('/activity', { params });
      if (res.status >= 400) throw new Error(`/activity failed: ${res.status}`);
      return res.data as ActivityLogPage;
    },
    enabled,
    staleTime: 20_000,
  });
}

// ─── /api/inspect (Phase B.2) ────────────────────────────────
export type InspectTagFilter =
  | 'all' | 'late' | 'early-leave' | 'late-leave' | 'over-8h' | 'under-8h' | 'tt' | 'absent' | 'conge';

export interface InspectRowDTO {
  date: string;
  pointage: { checkIn: string | null; checkOut: string | null };
  hoursReal: number | null;
  tags: DailyTagDTO[];
  decisionType: string | null; // built-in decision, custom AUTRE code, or derived CONGE
  notifiedInAdvance: NotifiedInAdvance | null;
  note: string | null;
  categories: InspectTagFilter[];
}
export type InspectDayKind =
  | 'worked' | 'incomplete' | 'tt' | 'conge' | 'recup' | 'maladie' | 'justifie'
  | 'absent' | 'ferie' | 'weekend' | 'hors-contrat' | 'futur';
export interface InspectDayDTO {
  date: string;
  dow: number;
  kind: InspectDayKind;
  hours: number | null;
  checkIn: string | null;
  checkOut: string | null;
  lateMinutes: number | null;
}
export interface InspectStatsDTO {
  totalHours: number; expectedHours: number; delta: number; workedDays: number;
  avgDailyHours: number | null; overtimeMinutes: number; undertimeMinutes: number;
  daysPresent: number; workableDays: number; presenceRate: number | null;
  lateCount: number; cumulativeLateMinutes: number; avgLateMinutes: number | null;
  punctualityRate: number | null;
  avgArrival: string | null; avgDeparture: string | null;
  earliestArrival: string | null; latestDeparture: string | null;
  expectedStart: string; expectedEnd: string; expectedDayHours: number;
  ttCount: number; congeCount: number; recupCount: number; maladieCount: number;
  justifiedCount: number; absentCount: number; incompleteCount: number;
  holidayCount: number; weekendCount: number;
}
export interface InspectResultDTO {
  employee: { id: string; code: string; name: string; entityId: string; role: string | null; entityShort: string };
  range: { start: string; end: string; workingDays: number };
  stats: InspectStatsDTO;
  counts: Record<InspectTagFilter, number>;
  rows: InspectRowDTO[];
  days: InspectDayDTO[];
}

export interface InspectEntityDayDTO {
  date: string;
  dow: number;
  weekend: boolean;
  ferie: boolean;
  futur: boolean;
  presents: number;
  incomplets: number;
  tt: number;
  conges: number;
  autres: number;
  absents: number;
  totalHours: number;
  avgHours: number | null;
}
export interface InspectEmployeeSummaryDTO {
  id: string; code: string; name: string; entityId: string; entityShort: string; role: string | null;
  workedDays: number; totalHours: number; expectedHours: number; delta: number;
  presenceRate: number | null; lateCount: number; avgLateMinutes: number | null;
  absentCount: number; congeCount: number; ttCount: number; incompleteCount: number;
  avgArrival: string | null;
}
export interface InspectEntityResultDTO {
  scope: { entityId: string; entityShort: string | null; employeeCount: number };
  range: { start: string; end: string; workingDays: number };
  stats: InspectStatsDTO;
  days: InspectEntityDayDTO[];
  employees: InspectEmployeeSummaryDTO[];
}

export function useInspectEntity(opts: {
  entityId: string; startDate: string; endDate: string; enabled?: boolean;
}) {
  return useQuery<InspectEntityResultDTO>({
    queryKey: ['inspect-entity', opts.entityId, opts.startDate, opts.endDate],
    queryFn: async () => {
      const res = await apiClient.get('/inspect/entity', {
        params: { entityId: opts.entityId, startDate: opts.startDate, endDate: opts.endDate },
      });
      if (res.status >= 400) throw new Error(`/inspect/entity failed: ${res.status}`);
      return res.data as InspectEntityResultDTO;
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useInspect(opts: {
  employeeId: string | null; startDate: string; endDate: string; tagFilter: InspectTagFilter; enabled?: boolean;
}) {
  return useQuery<InspectResultDTO>({
    queryKey: ['inspect', opts.employeeId, opts.startDate, opts.endDate, opts.tagFilter],
    queryFn: async () => {
      const res = await apiClient.get('/inspect', {
        params: { employeeId: opts.employeeId, startDate: opts.startDate, endDate: opts.endDate, tagFilter: opts.tagFilter },
      });
      if (res.status >= 400) throw new Error(`/inspect failed: ${res.status}`);
      return res.data as InspectResultDTO;
    },
    enabled: (opts.enabled ?? true) && !!opts.employeeId,
    staleTime: 30_000,
  });
}

// ─── /api/dashboard (Phase B.3) ──────────────────────────────
export interface KpiMetricDTO { value: number; delta: number; sparkline: number[] }
export interface DashboardKpisDTO {
  presents: KpiMetricDTO & { total: number };
  retards: KpiMetricDTO & { byEntity: Record<string, number> };
  absencesJustifiees: KpiMetricDTO & { breakdown: { conge: number; maladie: number } };
  absencesNonJustifiees: KpiMetricDTO;
}
export type DashboardView = 'day' | 'week';

export function useDashboardKpis(opts: { entityId: string; date: string; view?: DashboardView }) {
  const view = opts.view ?? 'day';
  return useQuery<DashboardKpisDTO>({
    queryKey: ['dashboard-kpis', opts.entityId, view, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/dashboard/kpis', { params: { entityId: opts.entityId, date: opts.date, view } });
      if (res.status >= 400) throw new Error(`kpis failed: ${res.status}`);
      return res.data as DashboardKpisDTO;
    },
    staleTime: 60_000,
  });
}

// ─── /api/dashboard/overview — état de la journée + inbox transverse ──
export type DashboardDayState =
  | 'workday' | 'weekend' | 'holiday' | 'future'
  // 'pending' = aujourd'hui, pointage pas encore importé ;
  // 'not-imported' = journée passée dont le fichier n'est jamais arrivé.
  | 'pending' | 'not-imported';
export interface DashboardDayDTO {
  date: string;
  state: DashboardDayState;
  holidayName: string | null;
  pointageImported: boolean;
  /** Entités de la portée dont le fichier manque alors que d'autres
   * l'ont reçu — leur équipe n'est comptée ni présente ni absente. */
  missingEntities: Array<{ id: string; short: string }>;
  counts: {
    roster: number; expected: number; present: number; retard: number; tt: number;
    conge: number; recup: number; maladie: number; autreJustifie: number; absent: number;
  };
}
export interface AwayPersonDTO {
  employeeId: string; name: string; entityId: string;
  kind: 'CONGE' | 'RECUP' | 'MALADIE' | 'TT' | 'AJ' | 'ANJ';
  until: string | null;
}
export interface TodoItemDTO {
  key: string; label: string; detail: string; count: number; href: string;
  tone: 'shf' | 'info' | 'warn' | 'bad';
}
export interface PendingValidationDayDTO {
  entityId: string;
  date: string;
  records: number;
  /** Réponses requises manquantes — 0 = la journée est prête à valider. */
  blockers: number;
}
export interface DashboardOverviewDTO {
  day: DashboardDayDTO;
  away: AwayPersonDTO[];
  todo: TodoItemDTO[];
  /** Journées ouvrées, pointage importé, pas encore verrouillées. */
  pendingValidation: PendingValidationDayDTO[];
}

export function useDashboardOverview(opts: { entityId: string; date: string }) {
  return useQuery<DashboardOverviewDTO>({
    queryKey: ['dashboard-overview', opts.entityId, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/dashboard/overview', {
        params: { entityId: opts.entityId, date: opts.date },
      });
      if (res.status >= 400) throw new Error(`overview failed: ${res.status}`);
      return res.data as DashboardOverviewDTO;
    },
    staleTime: 60_000,
  });
}

export interface FlaggedPersonDTO {
  employeeId: string; name: string; entityId: string; entityShort: string;
  type: 'retards' | 'sous-pointage' | 'absences'; count: number; impact: string;
  pattern: Array<'ok' | 'late' | 'short' | 'absent' | 'off' | 'none'>;
}
export function useDashboardFlagged(opts: { entityId: string; date: string }) {
  return useQuery<{ count: number; people: FlaggedPersonDTO[] }>({
    queryKey: ['dashboard-flagged', opts.entityId, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/dashboard/flagged', { params: { entityId: opts.entityId, date: opts.date } });
      if (res.status >= 400) throw new Error(`flagged failed: ${res.status}`);
      return res.data as { count: number; people: FlaggedPersonDTO[] };
    },
    staleTime: 60_000,
  });
}

export interface ActivityItemDTO {
  id: string; action: string; targetType: string | null; createdAt: string;
  user: { id: string; name: string; email: string } | null;
  payload: Record<string, unknown> | null;
  subjectName?: string | null;
  entityName?: string | null;
}
export function useActivity(limit = 8) {
  return useQuery<ActivityItemDTO[]>({
    queryKey: ['activity', limit],
    queryFn: async () => {
      const res = await apiClient.get('/activity', { params: { limit } });
      if (res.status >= 400) throw new Error(`activity failed: ${res.status}`);
      // GET /api/activity returns { total, limit, offset, entries }.
      // (This previously read `.items`, which is always undefined → the
      // dashboard activity feed silently rendered empty.)
      return (res.data as { entries: ActivityItemDTO[] }).entries ?? [];
    },
    staleTime: 30_000,
  });
}

// ─── /api/decision-types (Phase E.1.3 — merged built-in + custom) ──
export interface DecisionTypeDTO {
  code: string;
  label: string;
  builtin: boolean;
  id: string | null;
}

export function useDecisionTypes() {
  return useQuery<DecisionTypeDTO[]>({
    queryKey: ['decision-types'],
    queryFn: async () => {
      const res = await apiClient.get('/decision-types');
      if (res.status >= 400) throw new Error(`/decision-types failed: ${res.status}`);
      return res.data as DecisionTypeDTO[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateDecisionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { label: string; code?: string }) => {
      const res = await apiClient.post('/decision-types', args);
      if (res.status >= 400) {
        const err = new Error(`create decision-type failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as DecisionTypeDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decision-types'] }),
  });
}

// Renommage seul : le `code` est immuable (il est estampillé sur les
// pointages), donc l'historique reste rattaché au même type.
export function useUpdateDecisionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; label: string }) => {
      const res = await apiClient.patch(`/decision-types/${args.id}`, { label: args.label });
      if (res.status >= 400) {
        const err = new Error(`update decision-type failed: ${res.status}`);
        (err as Error & { body?: unknown }).body = res.data;
        throw err;
      }
      return res.data as DecisionTypeDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decision-types'] }),
  });
}

export function useDeleteDecisionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/decision-types/${id}`);
      if (res.status >= 400) throw new Error(`delete decision-type failed: ${res.status}`);
      return res.data as { id: string; removed: boolean };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decision-types'] }),
  });
}

// ─── /api/holidays (for the leave calendar grey-out) ────────
export function useHolidays(from: string, to: string) {
  return useQuery<Array<{ date: string; name: string }>>({
    queryKey: ['holidays', from, to],
    queryFn: async () => {
      const res = await apiClient.get('/holidays', { params: { from, to } });
      if (res.status >= 400) throw new Error(`holidays failed: ${res.status}`);
      return res.data as Array<{ date: string; name: string }>;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── /api/leaves (Phase C) ───────────────────────────────────
export type LeaveType = 'CONGE' | 'RECUP';
export interface LeaveBalanceDTO {
  employeeId: string; type: LeaveType; initialStock: number; accrued: number;
  manualAdded: number; consumed: number; current: number; updatedAt: string;
}
export interface LeaveBalanceRow {
  employee: { id: string; code: string; name: string; entityId: string; role: string | null };
  balance: LeaveBalanceDTO;
}
export interface LeaveTxDTO {
  id: string; employeeId: string; type: LeaveType; kind: string; amount: number;
  date: string; reason: string | null; decidedBy: string | null; decidedAt: string; source: string;
}
export interface LeaveRequestDTO {
  id: string; employee: { id: string; name: string; entityId: string }; type: LeaveType;
  startDate: string; endDate: string; daysCount: number; reason: string | null; stage: string;
  proposedBy: string | null; proposedByName: string | null;
}

export function useLeaveBalances(opts: { type: LeaveType; entityId: string }) {
  return useQuery<LeaveBalanceRow[]>({
    queryKey: ['leave-balances', opts.type, opts.entityId],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/balances', { params: { type: opts.type, entityId: opts.entityId } });
      if (res.status >= 400) throw new Error(`balances failed: ${res.status}`);
      return res.data as LeaveBalanceRow[];
    },
    staleTime: 30_000,
  });
}
export function useLeaveTransactions(opts: { type: LeaveType; employeeId?: string }) {
  return useQuery<LeaveTxDTO[]>({
    queryKey: ['leave-tx', opts.type, opts.employeeId ?? 'all'],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/transactions', { params: { type: opts.type, ...(opts.employeeId ? { employeeId: opts.employeeId } : {}) } });
      if (res.status >= 400) throw new Error(`tx failed: ${res.status}`);
      return res.data as LeaveTxDTO[];
    },
    staleTime: 30_000,
  });
}
export function usePendingRequests(opts: { entityId: string; enabled?: boolean }) {
  return useQuery<LeaveRequestDTO[]>({
    queryKey: ['leave-requests', 'PROPOSED_BY_LAMIS', opts.entityId],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/requests', { params: { stage: 'PROPOSED_BY_LAMIS', entityId: opts.entityId } });
      if (res.status >= 400) throw new Error(`requests failed: ${res.status}`);
      return res.data as LeaveRequestDTO[];
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}
/** Une demande, telle que le tiroir employé la montre — toutes étapes
 * confondues, avec qui l'a proposée et d'où elle vient. */
export interface EmployeeLeaveRequestDTO {
  id: string; type: LeaveType; startDate: string; endDate: string; daysCount: number;
  reason: string | null; stage: string;
  proposedByName: string | null; decidedByName: string | null;
  decidedAt: string | null; rejectionReason: string | null; createdAt: string;
  /** 'espace' = déposée par l'employé depuis son espace. */
  origin: 'espace' | 'staff';
}
export interface EmployeeClaimDTO {
  id: string; date: string; amount: number; motif: string | null; stage: string;
  rejectionReason: string | null; createdAt: string;
}
export interface LeaveDetailDTO {
  employee: { id: string; code: string; name: string; role: string | null; entityShort: string };
  balance: LeaveBalanceDTO | null;
  accrualRule: string;
  transactions: LeaveTxDTO[];
  requests: EmployeeLeaveRequestDTO[];
  claims: EmployeeClaimDTO[];
  /** Non nul quand la personne est absente aujourd'hui. */
  onLeaveNow: { type: LeaveType; from: string; until: string | null } | null;
  pendingRequests: Array<{ id: string; startDate: string; endDate: string; daysCount: number; stage: string }>;
}

export function useLeaveDetail(employeeId: string | null, type: LeaveType) {
  return useQuery({
    queryKey: ['leave-detail', employeeId, type],
    queryFn: async () => {
      const res = await apiClient.get(`/leaves/balances/${employeeId}/${type}`);
      if (res.status >= 400) throw new Error(`detail failed: ${res.status}`);
      return res.data as LeaveDetailDTO;
    },
    enabled: !!employeeId,
  });
}
export function useLeaveChart(employeeId: string | null, type: LeaveType, year: number) {
  return useQuery({
    queryKey: ['leave-chart', employeeId, type, year],
    queryFn: async () => {
      const res = await apiClient.get(`/leaves/balances/${employeeId}/${type}/chart`, { params: { year } });
      if (res.status >= 400) throw new Error(`chart failed: ${res.status}`);
      return res.data as { events: Array<{ date: string; balance: number; cumulative_consumed: number; kind: string; amount: number }> };
    },
    enabled: !!employeeId,
  });
}

// ─── vue d'ensemble Congés + provenance des jours posés ──────
export interface OnLeavePersonDTO {
  employeeId: string; name: string; entityId: string; role: string | null;
  type: LeaveType; from: string; until: string | null; daysLeft: number | null; balance: number;
}
export type CongeOrigin = 'demande' | 'saisie' | 'quotidien' | 'arbitrage';
export interface CongeDayDTO {
  employeeId: string; employeeName: string; entityId: string; date: string;
  type: LeaveType; amount: number; origin: CongeOrigin;
  ledgered: boolean; awaitingValidation: boolean; label: string;
}
export interface NegativeBalanceDTO {
  employeeId: string; name: string; entityId: string; role: string | null;
  type: LeaveType; balance: number;
}
export interface LeavesOverviewDTO {
  today: string;
  totals: {
    employees: number; stockInitial: number; acquis: number; consomme: number;
    solde: number; recupSolde: number; low: number;
  };
  pending: { conge: number; recup: number; claims: number; days: number };
  onLeave: OnLeavePersonDTO[];
  upcoming: OnLeavePersonDTO[];
  toRegularize: CongeDayDTO[];
  /** Soldes sous zéro — le débit est passé, le crédit manque. */
  negative: NegativeBalanceDTO[];
  recent: LeaveTxDTO[];
}

export function useLeavesOverview(opts: { entityId: string }) {
  return useQuery<LeavesOverviewDTO>({
    queryKey: ['leaves-overview', opts.entityId],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/overview', { params: { entityId: opts.entityId } });
      if (res.status >= 400) throw new Error(`leaves overview failed: ${res.status}`);
      return res.data as LeavesOverviewDTO;
    },
    staleTime: 30_000,
  });
}

export function useCongeDays(opts: { entityId: string; from?: string; to?: string; enabled?: boolean }) {
  return useQuery<CongeDayDTO[]>({
    queryKey: ['conge-days', opts.entityId, opts.from ?? '', opts.to ?? ''],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/conge-days', {
        params: { entityId: opts.entityId, ...(opts.from ? { from: opts.from } : {}), ...(opts.to ? { to: opts.to } : {}) },
      });
      if (res.status >= 400) throw new Error(`conge-days failed: ${res.status}`);
      return res.data as CongeDayDTO[];
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export interface RegularizeResultDTO {
  applied: Array<{ employeeId: string; date: string; type: LeaveType; amount: number; balanceAfter: number }>;
  skipped: Array<{ employeeId: string; date: string; reason: string }>;
}
export function useRegularizeLeaves() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: Array<{ employeeId: string; date: string }>) => {
      const res = await apiClient.post('/leaves/regularize', { items });
      if (res.status >= 400) { const e = new Error('regularize'); (e as Error & { body?: unknown }).body = res.data; throw e; }
      return res.data as RegularizeResultDTO;
    },
    onSuccess: () => invalidateLeaves(qc),
  });
}

function invalidateLeaves(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['leave-balances'] });
  qc.invalidateQueries({ queryKey: ['leave-tx'] });
  qc.invalidateQueries({ queryKey: ['leave-requests'] });
  qc.invalidateQueries({ queryKey: ['leave-detail'] });
  qc.invalidateQueries({ queryKey: ['leave-chart'] });
  qc.invalidateQueries({ queryKey: ['leaves-overview'] });
  qc.invalidateQueries({ queryKey: ['conge-days'] });
}
export function useConsumeLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; type: LeaveType; startDate: string; endDate: string; reason?: string }) => {
      const res = await apiClient.post('/leaves/consume', args);
      if (res.status >= 400) { const e = new Error('consume'); (e as Error & { body?: unknown }).body = res.data; throw e; }
      return res.data as { days: number; current: number };
    },
    onSuccess: () => invalidateLeaves(qc),
  });
}
export function useManualTx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; type: LeaveType; kind: 'MANUAL_ADDITION'; amount: number; date: string; reason?: string }) => {
      const res = await apiClient.post('/leaves/transactions', args);
      if (res.status >= 400) { const e = new Error('tx'); (e as Error & { body?: unknown }).body = res.data; throw e; }
      return res.data as { current: number };
    },
    onSuccess: () => invalidateLeaves(qc),
  });
}
export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.patch(`/leaves/requests/${id}/approve`, {});
      if (res.status >= 400) { const e = new Error('approve'); (e as Error & { body?: unknown }).body = res.data; throw e; }
      return res.data;
    },
    onSuccess: () => invalidateLeaves(qc),
  });
}
export function useRejectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; rejectionReason?: string }) => {
      const res = await apiClient.patch(`/leaves/requests/${args.id}/reject`, { rejectionReason: args.rejectionReason });
      if (res.status >= 400) { const e = new Error('reject'); (e as Error & { body?: unknown }).body = res.data; throw e; }
      return res.data;
    },
    onSuccess: () => invalidateLeaves(qc),
  });
}

// ─── /api/attendance/bulk ────────────────────────────────────

export interface BulkEntry {
  date: string;
  employeeId: string;
  checkIn?: string | null;
  checkOut?: string | null;
  status?: string;
  note?: string | null;
}

export function useBulkAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: BulkEntry[]) => {
      const res = await apiClient.post('/attendance/bulk', { entries });
      if (res.status >= 400) throw new Error(`POST /attendance/bulk failed: ${res.status}`);
      return res.data as { applied: number; dateRange: { from: string; to: string } | null };
    },
    onSuccess: (_data, vars) => {
      const dates = new Set(vars.map((v) => v.date));
      for (const d of dates) {
        qc.invalidateQueries({ queryKey: ['attendance', 'daily', d] });
        qc.invalidateQueries({ queryKey: ['followups', 'daily', d] });
      }
    },
  });
}

// ─── /api/attendance/import ──────────────────────────────────

// Pourquoi une ligne n'est pas un premier import: 'punches' (la journée
// a déjà des pointages), 'decision' (elle a été arbitrée), 'validated'
// (elle est signée — refusée), 'paie' (une décision de la grille repose
// dessus), 'corrected' (une heure corrigée à la main — conservée).
export type ImportOverlapKind = 'punches' | 'decision' | 'validated' | 'paie' | 'corrected';

export interface ImportOverlapSummary {
  rows: number;
  employees: number;
  dates: string[];
  arbitrated: number;
  validated: number;
  paieCells: number;
  /** Lignes avec une heure corrigée à la main: la correction est gardée,
   *  seule l'heure pointeuse en dessous suit le fichier. Absent des
   *  anciens aperçus. */
  corrected?: number;
}

export interface ImportPreviewRecord {
  employeeId: string;
  employeeName: string;
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  action: 'create' | 'update-times' | 'preserve-status' | 'skip-validated';
  existingStatus?: string;
  overlap?: ImportOverlapKind[];
  existingCheckIn?: string | null;
  existingCheckOut?: string | null;
  existingDecision?: string | null;
}

export interface ImportPreview {
  matchedRecords: ImportPreviewRecord[];
  unmatchedNames: string[];
  /** Salariés « hors pointage » vus dans le fichier: lignes ignorées,
   *  et surtout pas rangées parmi les noms inconnus. */
  pointageExcludedNames: string[];
  dateRange: { from: string; to: string } | null;
  fileResults: Array<{ filename: string; sheet: string; rowsParsed: number; rowsSkipped: number }>;
  totals: {
    rowsParsed: number;
    rowsMatched: number;
    rowsUnmatched: number;
    distinctDates: number;
    distinctEmployees: number;
    validatedConflicts: number;
  };
  /** Null quand toutes les dates sont vierges — le chemin d'import ordinaire. */
  overlap: ImportOverlapSummary | null;
}

export interface ImportCommitResult {
  inserted: number;
  updatedTimes: number;
  preservedStatus: number;
  skippedUnmatched: number;
  skippedValidated: number;
  refusedValidated: Array<{ employeeName: string; date: string }>;
  clearedDecisions: Array<{ employeeName: string; date: string; decision: string }>;
  staleGridCells: number;
  overlap: ImportOverlapSummary | null;
  dateRange: { from: string; to: string } | null;
}

export function useImportPreview() {
  return useMutation({
    mutationFn: async (args: { files: File[]; entityId: string }) => {
      const form = new FormData();
      for (const f of args.files) form.append('files', f);
      // The server requires this field — it scopes the name match
      // to the selected entity. The "All entities" sentinel ('all')
      // is rejected at the route layer; the UI prevents that from
      // reaching here by disabling the import button when the
      // EntityBar is on "All".
      form.append('entityId', args.entityId);
      const res = await apiClient.post('/attendance/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 400) {
        const err = new Error(`Import preview failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { previewToken: string; preview: ImportPreview };
    },
  });
}

export function useImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    // `confirmOverwrite` n'est envoyé que depuis l'écran de confirmation
    // d'écrasement — le chemin ordinaire ne le porte jamais.
    mutationFn: async (args: { previewToken: string; confirmOverwrite?: boolean }) => {
      const res = await apiClient.post('/attendance/import/commit', {
        previewToken: args.previewToken,
        ...(args.confirmOverwrite ? { confirmOverwrite: true } : {}),
      });
      if (res.status >= 400) {
        const err = new Error(`Import commit failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as ImportCommitResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['followups'] });
    },
  });
}

// ─── /api/followups ──────────────────────────────────────────

export function useFollowups(opts: {
  date: string;
  entityId: string;
  pollMs?: number;
  enabled?: boolean;
}) {
  return useQuery<FollowupItem[]>({
    queryKey: ['followups', 'daily', opts.date, opts.entityId],
    queryFn: async () => {
      const params: Record<string, string> = { date: opts.date };
      if (opts.entityId && opts.entityId !== 'all') params.entityId = opts.entityId;
      const res = await apiClient.get('/followups', { params });
      if (res.status >= 400) throw new Error(`/followups failed: ${res.status}`);
      return res.data as FollowupItem[];
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.pollMs ?? false,
    staleTime: 30_000,
  });
}

// ─── /api/followups/draft ────────────────────────────────────
// Le brouillon pré-rempli du modal de composition. Les gabarits FR
// restent serveur: un message envoyé au nom de quelqu'un se rédige à
// partir des faits que le serveur a.
export interface FollowupDraftDTO {
  employee: { id: string; name: string; email: string | null };
  triggerDate: string;
  kinds: string[];
  subject: string;
  body: string;
  items: Array<{ kind: string; label: string; detail: string; severity: string }>;
  alreadySent: string[];
}

export function useFollowupDraft(opts: { employeeId: string | null; date: string }) {
  return useQuery<FollowupDraftDTO>({
    queryKey: ['followup-draft', opts.employeeId, opts.date],
    queryFn: async () => {
      const res = await apiClient.get('/followups/draft', {
        params: { employeeId: opts.employeeId, date: opts.date },
      });
      if (res.status >= 400) {
        const err = new Error(`followup draft failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as FollowupDraftDTO;
    },
    enabled: opts.employeeId != null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSendFollowupEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      employeeId: string;
      triggerDate: string;
      kinds: string[];
      subject?: string;
      body?: string;
    }) => {
      const res = await apiClient.post('/followups/email', args);
      if (res.status >= 400) {
        const err = new Error(`send email failed: ${res.status}`);
        (err as Error & { body?: unknown; status?: number }).body = res.data;
        (err as Error & { body?: unknown; status?: number }).status = res.status;
        throw err;
      }
      return res.data as { status: 'SENT'; gmailId: string; kinds: string[] };
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['followups', 'daily', vars.triggerDate] });
      qc.invalidateQueries({ queryKey: ['email-history', vars.employeeId] });
    },
  });
}

// ─── Payroll (Salaires & Bonus) ──────────────────────────────
// DTOs live in @pointage/shared (payroll.ts). Every mutation
// invalidates the whole ['payroll'] scope — the month payload is a
// single aggregate, cheap to refetch.

function payrollError(label: string, res: { status: number; data: unknown }): Error {
  const err = new Error(`${label} failed: ${res.status}`);
  (err as Error & { body?: unknown; status?: number }).body = res.data;
  (err as Error & { body?: unknown; status?: number }).status = res.status;
  return err;
}

export function usePayrollMonth(opts: { year: number; month: number; enabled?: boolean }) {
  return useQuery<PayrollMonthDTO>({
    queryKey: ['payroll', opts.year, opts.month],
    queryFn: async () => {
      const res = await apiClient.get('/payroll/month', { params: { year: opts.year, month: opts.month } });
      if (res.status >= 400) throw payrollError('payroll month', res);
      return res.data as PayrollMonthDTO;
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function usePatchPayrollLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number; employeeId: string; status: 'SAISI' | 'VALIDE' }) => {
      const res = await apiClient.patch('/payroll/line', args);
      if (res.status >= 400) throw payrollError('payroll line', res);
      return res.data as { employeeId: string; status: PayrollLineStatus };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// Mode de paiement PERMANENT de l'employé (virement / chèque / à part),
// réglé depuis la page Salaires ; le mois sert au verrou de revue.
export function usePatchPayrollPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number; employeeId: string; method: PaymentMethod }) => {
      const res = await apiClient.patch('/payroll/payment-method', args);
      if (res.status >= 400) throw payrollError('payroll payment method', res);
      return res.data as { employeeId: string; paymentMethod: PaymentMethod };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
  });
}

export function usePatchPayrollNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      year: number; month: number; employeeId: string;
      text?: string; amount?: number | null; tags?: PayrollTag[];
    }) => {
      const res = await apiClient.patch('/payroll/note', args);
      if (res.status >= 400) throw payrollError('payroll note', res);
      return res.data as PayrollNoteDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// Per-month salary override on the Salaires page. amount:null clears the
// override and reverts the month to the reference salary (Fiche Employé).
// Grille paie: the validated grilles' paie cells (amounts come from
// usePayrollMonth). Shares the ['payroll'] key prefix, so every payroll
// mutation refreshes it too.
export function usePayrollGrid(opts: { year: number; month: number; enabled?: boolean }) {
  return useQuery<PayrollGridDTO>({
    queryKey: ['payroll', 'grid', opts.year, opts.month],
    queryFn: async () => {
      const res = await apiClient.get('/payroll/grid', { params: { year: opts.year, month: opts.month } });
      if (res.status >= 400) throw payrollError('payroll grid', res);
      return res.data as PayrollGridDTO;
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function usePatchPayrollSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number; employeeId: string; amount: number | null }) => {
      const res = await apiClient.patch('/payroll/salary', args);
      if (res.status >= 400) throw payrollError('payroll salary', res);
      return res.data as { employeeId: string; salaryOverride: number | null; referenceSalary: number; salary: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

export function usePatchPayrollBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; quarter: number; employeeId: string; amount: number | null }) => {
      const res = await apiClient.patch('/payroll/bonus', args);
      if (res.status >= 400) throw payrollError('payroll bonus', res);
      return res.data as { employeeId: string; finalBonus: number | null };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function usePatchPayrollInformed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; quarter: number; employeeId: string; informed: boolean }) => {
      const res = await apiClient.patch('/payroll/informed', args);
      if (res.status >= 400) throw payrollError('payroll informed', res);
      return res.data as { employeeId: string; informed: boolean };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// Étape 1 — le préparateur remet le mois (payroll.edit). `force` lève
// une grille mensuelle non validée : réservé au valideur, le serveur
// revérifie.
export function useSubmitPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number; force?: boolean }) => {
      const res = await apiClient.post('/payroll/submit', args);
      if (res.status >= 400) throw payrollError('payroll submit', res);
      return res.data as { stage: PayrollStage };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// Étape 2 — la seconde signature (payroll.validate), celle qui
// débloque les fichiers banque.
export function useValidatePayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number }) => {
      const res = await apiClient.post('/payroll/validate', args);
      if (res.status >= 400) throw payrollError('payroll validate', res);
      return res.data as { stage: PayrollStage };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// Le valideur rend la main au préparateur au lieu de corriger lui-même.
export function useSendBackPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number; reason?: string }) => {
      const res = await apiClient.post('/payroll/send-back', args);
      if (res.status >= 400) throw payrollError('payroll send-back', res);
      return res.data as { stage: PayrollStage };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function useMarkPayrollSent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number }) => {
      const res = await apiClient.post('/payroll/mark-sent', args);
      if (res.status >= 400) throw payrollError('payroll mark-sent', res);
      return res.data as { stage: PayrollStage };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function useReopenPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; month: number }) => {
      const res = await apiClient.post('/payroll/reopen', args);
      if (res.status >= 400) throw payrollError('payroll reopen', res);
      return res.data as { stage: PayrollStage };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function useEmployeeSalaries(employeeId: string | null) {
  return useQuery<SalaryHistoryEntryDTO[]>({
    queryKey: ['employee-salaries', employeeId],
    queryFn: async () => {
      const res = await apiClient.get(`/employees/${employeeId}/salaries`);
      if (res.status >= 400) throw payrollError('employee salaries', res);
      return res.data as SalaryHistoryEntryDTO[];
    },
    enabled: !!employeeId,
    staleTime: 60_000,
  });
}

// ─── /api/me + admin claims/docs (Espace employé) ────────────
export interface MeOverview {
  employee: {
    id: string; code: string; name: string; department: string | null; email: string | null;
    entity: { id: string; name: string; short: string; color: string };
    hiredAt: string | null;
  };
  balances: { conge: LeaveBalanceDTO | null; recup: LeaveBalanceDTO | null };
  accrualRule: string;
}
export type MeLeaveStage = 'PROPOSED_BY_LAMIS' | 'APPROVED' | 'REJECTED' | 'DEBITED';
export interface MeLeaveRequestDTO {
  id: string; type: LeaveType; startDate: string; endDate: string; daysCount: number;
  reason: string | null; stage: MeLeaveStage; rejectionReason: string | null;
  createdAt: string; decidedAt: string | null;
}
export type ClaimStage = 'PENDING' | 'APPROVED' | 'REJECTED';
export interface MeClaimDTO {
  id: string; date: string; amount: number; motif: string | null; stage: ClaimStage;
  rejectionReason: string | null; createdAt: string; decidedAt: string | null;
}
export type DocRequestType = 'ATTESTATION_TRAVAIL' | 'ATS' | 'ATTESTATION_CONGE' | 'FICHE_PAIE' | 'AUTRE';
export type DocRequestForme = 'SCAN' | 'ORIGINAL';
export type DocRequestStatus = 'RECUE' | 'PREPARATION' | 'SIGNATURE' | 'PRETE' | 'ENVOYEE' | 'REFUSEE';
export interface MeDocRequestDTO {
  id: string; type: DocRequestType; forme: DocRequestForme; status: DocRequestStatus;
  months: string[]; leaveStart: string | null; leaveEnd: string | null; salaryMonths: number | null;
  dest: string | null; lang: string; copies: number; note: string | null;
  createdAt: string; doneAt: string | null;
}
export interface MeRequests { leaves: MeLeaveRequestDTO[]; claims: MeClaimDTO[]; docs: MeDocRequestDTO[] }

export function useMeOverview(enabled = true) {
  // 404 = signed-in account has no employee fiche (staff without a
  // matching email) — a normal state, surfaced as `null`, not an error.
  return useQuery<MeOverview | null>({
    queryKey: ['me-overview'],
    queryFn: async () => {
      const res = await apiClient.get('/me/overview');
      if (res.status === 404) return null;
      if (res.status >= 400) throw new Error(`me/overview failed: ${res.status}`);
      return res.data as MeOverview;
    },
    enabled,
    staleTime: 30_000,
  });
}
export function useMeChart(year: number, enabled = true) {
  return useQuery({
    queryKey: ['me-chart', year],
    queryFn: async () => {
      const res = await apiClient.get('/me/chart', { params: { year } });
      if (res.status >= 400) throw new Error(`me/chart failed: ${res.status}`);
      return res.data as { events: Array<{ date: string; balance: number; kind: string; amount: number }> };
    },
    enabled,
    staleTime: 60_000,
  });
}
export function useMeHolidays(from: string, to: string) {
  return useQuery<Array<{ date: string; name: string }>>({
    queryKey: ['me-holidays', from, to],
    queryFn: async () => {
      const res = await apiClient.get('/me/holidays', { params: { from, to } });
      if (res.status >= 400) throw new Error(`me/holidays failed: ${res.status}`);
      return res.data as Array<{ date: string; name: string }>;
    },
    staleTime: 10 * 60_000,
  });
}
export function useMeRequests(enabled = true) {
  return useQuery<MeRequests>({
    queryKey: ['me-requests'],
    queryFn: async () => {
      const res = await apiClient.get('/me/requests');
      if (res.status >= 400) throw new Error(`me/requests failed: ${res.status}`);
      return res.data as MeRequests;
    },
    enabled,
    staleTime: 15_000,
  });
}

function invalidateMe(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['me-overview'] });
  qc.invalidateQueries({ queryKey: ['me-requests'] });
  qc.invalidateQueries({ queryKey: ['me-chart'] });
}
const throwBody = (label: string, res: { status: number; data: unknown }) => {
  const e = new Error(label);
  (e as Error & { body?: unknown }).body = res.data;
  throw e;
};

export function useCreateMyLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { type: LeaveType; startDate: string; endDate: string; reason?: string }) => {
      const res = await apiClient.post('/me/leave-requests', args);
      if (res.status >= 400) throwBody('leave-request', res);
      return res.data as MeLeaveRequestDTO;
    },
    onSuccess: () => invalidateMe(qc),
  });
}
export function useCancelMyLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/me/leave-requests/${id}`);
      if (res.status >= 400) throwBody('cancel', res);
    },
    onSuccess: () => invalidateMe(qc),
  });
}
export function useCreateMyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { date: string; halfDay?: boolean; motif?: string }) => {
      const res = await apiClient.post('/me/claims', args);
      if (res.status >= 400) throwBody('claim', res);
      return res.data as MeClaimDTO;
    },
    onSuccess: () => invalidateMe(qc),
  });
}
export function useCancelMyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/me/claims/${id}`);
      if (res.status >= 400) throwBody('cancel', res);
    },
    onSuccess: () => invalidateMe(qc),
  });
}
export function useCreateMyDocRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: Partial<MeDocRequestDTO> & { type: DocRequestType }) => {
      const res = await apiClient.post('/me/doc-requests', args);
      if (res.status >= 400) throwBody('doc-request', res);
      return res.data as MeDocRequestDTO;
    },
    onSuccess: () => invalidateMe(qc),
  });
}
export function useCancelMyDocRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/me/doc-requests/${id}`);
      if (res.status >= 400) throwBody('cancel', res);
    },
    onSuccess: () => invalidateMe(qc),
  });
}

// ─── staff side: worked-day claims + document requests ───────
export interface AdminClaimDTO {
  id: string; employee: { id: string; name: string; entityId: string };
  date: string; amount: number; motif: string | null; stage: ClaimStage; createdAt: string;
}
export function useRecupClaims(opts: { entityId: string; stage?: ClaimStage }) {
  return useQuery<AdminClaimDTO[]>({
    queryKey: ['recup-claims', opts.stage ?? 'PENDING', opts.entityId],
    queryFn: async () => {
      const res = await apiClient.get('/leaves/claims', { params: { stage: opts.stage ?? 'PENDING', entityId: opts.entityId } });
      if (res.status >= 400) throw new Error(`claims failed: ${res.status}`);
      return res.data as AdminClaimDTO[];
    },
    staleTime: 30_000,
  });
}
export function useApproveClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.patch(`/leaves/claims/${id}/approve`, {});
      if (res.status >= 400) throwBody('approve-claim', res);
      return res.data;
    },
    onSuccess: () => { invalidateLeaves(qc); qc.invalidateQueries({ queryKey: ['recup-claims'] }); },
  });
}
export function useRejectClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; rejectionReason?: string }) => {
      const res = await apiClient.patch(`/leaves/claims/${args.id}/reject`, { rejectionReason: args.rejectionReason });
      if (res.status >= 400) throwBody('reject-claim', res);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recup-claims'] }),
  });
}
export interface AdminDocRequestDTO extends MeDocRequestDTO {
  employee: { id: string; name: string; entityId: string };
}
export function useDocRequests(opts: { entityId: string; scope?: 'open' | 'done' | 'all' }) {
  return useQuery<AdminDocRequestDTO[]>({
    queryKey: ['doc-requests', opts.scope ?? 'open', opts.entityId],
    queryFn: async () => {
      const res = await apiClient.get('/documents/requests', { params: { scope: opts.scope ?? 'open', entityId: opts.entityId } });
      if (res.status >= 400) throw new Error(`doc-requests failed: ${res.status}`);
      return res.data as AdminDocRequestDTO[];
    },
    staleTime: 30_000,
  });
}
export function useUpdateDocRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: DocRequestStatus }) => {
      const res = await apiClient.patch(`/documents/requests/${args.id}`, { status: args.status });
      if (res.status >= 400) throwBody('doc-status', res);
      return res.data as MeDocRequestDTO;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doc-requests'] }),
  });
}

// ─── /api/contracts (Période d'essai & CDD) ──────────────────
export interface ContractMilestoneDTO {
  code: 'mi' | 'j90' | 'j30' | 'j7';
  date: string; // YYYY-MM-DD — when the reminder becomes due
  state: {
    status: 'sent' | 'done' | 'snoozed';
    snoozeUntil: string | null;
    actedAt: string;
    actedBy: string | null;
  } | null;
}
export interface ContractEmployeeDTO {
  id: string;
  code: string;
  name: string;
  entityId: string;
  department: string | null;
  active: boolean;
}
export interface ContractEssaiDTO {
  employee: ContractEmployeeDTO;
  start: string;
  months: number;
  renewMonths: number | null;
  term: string;
  status: 'encours' | 'confirme' | 'rompu';
  outcomeAt: string | null;
  dateSortie: string | null;
  notes: string | null;
  milestones: ContractMilestoneDTO[];
}
export interface ContractCddDTO {
  employee: ContractEmployeeDTO;
  start: string | null;
  end: string;
  motif: string | null;
  renewals: number;
  status: 'actif' | 'cdi' | 'nonrenouvele' | 'termine';
  outcomeAt: string | null;
  dateSortie: string | null;
  notes: string | null;
  milestones: ContractMilestoneDTO[];
}
export interface ContractsDTO {
  today: string;
  essais: ContractEssaiDTO[];
  cdds: ContractCddDTO[];
}
export function useContracts(entityId: string) {
  return useQuery<ContractsDTO>({
    queryKey: ['contracts', entityId],
    queryFn: async () => {
      const res = await apiClient.get('/contracts', { params: { entityId } });
      if (res.status >= 400) throw new Error(`/contracts failed: ${res.status}`);
      return res.data as ContractsDTO;
    },
    staleTime: 30_000,
  });
}
export function useContractMilestoneAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      employeeId: string;
      kind: 'essai' | 'cdd';
      code: 'mi' | 'j90' | 'j30' | 'j7';
      action: 'send' | 'done' | 'snooze';
    }) => {
      const res = await apiClient.post(`/contracts/${args.employeeId}/milestones`, {
        kind: args.kind, code: args.code, action: args.action,
      });
      if (res.status >= 400) throwBody('contract-milestone', res);
      return res.data as { ok: true; to: string | null; milestone: ContractMilestoneDTO };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
}
export function useContractDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      employeeId: string;
      kind: 'essai' | 'cdd';
      decision: 'confirme' | 'renouvele' | 'rompu' | 'cdi' | 'nonrenouvele';
      date: string;
      months?: number;
      offDone?: number;
    }) => {
      const { employeeId, ...body } = args;
      const res = await apiClient.post(`/contracts/${employeeId}/decision`, body);
      if (res.status >= 400) throwBody('contract-decision', res);
      return res.data as { ok: true; essai: ContractEssaiDTO | null; cdd: ContractCddDTO | null };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// ─── /api/evaluations (Évaluations annuelles — staff) ────────
export function useEvaluations(opts: { year: number; enabled?: boolean }) {
  return useQuery<EvalOverviewDTO>({
    queryKey: ['evaluations', opts.year],
    queryFn: async () => {
      const res = await apiClient.get('/evaluations', { params: { year: opts.year } });
      if (res.status >= 400) throw new Error(`/evaluations failed: ${res.status}`);
      return res.data as EvalOverviewDTO;
    },
    enabled: opts.enabled !== false,
    staleTime: 15_000,
  });
}
function invalidateEvaluations(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['evaluations'] });
  qc.invalidateQueries({ queryKey: ['me-evaluation'] });
}
export function useSaveMgrEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number; notes: EvalNotes; comment?: string }) => {
      const { employeeId, ...body } = args;
      const res = await apiClient.put(`/evaluations/${employeeId}/mgr`, body);
      if (res.status >= 400) throwBody('mgr-save', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useSubmitMgrEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number }) => {
      const res = await apiClient.post(`/evaluations/${args.employeeId}/mgr/submit`, { year: args.year });
      if (res.status >= 400) throwBody('mgr-submit', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useProposeOtoSlots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number; slots: EvalSlot[] }) => {
      const res = await apiClient.post(`/evaluations/${args.employeeId}/oto/propose`, { year: args.year, slots: args.slots });
      if (res.status >= 400) throwBody('oto-propose', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useCloseEvalSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number; notes: EvalNotes; cr: string }) => {
      const res = await apiClient.post(`/evaluations/${args.employeeId}/oto/close`, { year: args.year, notes: args.notes, cr: args.cr });
      if (res.status >= 400) throwBody('oto-close', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useSignEvalMgr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number }) => {
      const res = await apiClient.post(`/evaluations/${args.employeeId}/sign`, { year: args.year });
      if (res.status >= 400) throwBody('sign', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useRemindEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number }) => {
      const res = await apiClient.post(`/evaluations/${args.employeeId}/remind`, { year: args.year });
      if (res.status >= 400) throwBody('remind', res);
      return res.data as { ok: true; to: string; remindedAt: string };
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useCreateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number; cat: string; title: string; description?: string; kpi: string; deadline: string }) => {
      const { employeeId, ...body } = args;
      const res = await apiClient.post(`/evaluations/${employeeId}/objectives`, body);
      if (res.status >= 400) throwBody('objective-create', res);
      return res.data as ObjectiveDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export type ObjectiveAction =
  | { action: 'validate' }
  | { action: 'refuse' }
  | { action: 'check'; q: EvalQuarter; prog: number; status: ObjectiveCheckStatus; note?: string };
export function useObjectiveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string } & ObjectiveAction) => {
      const { id, ...body } = args;
      const res = await apiClient.patch(`/evaluations/objectives/${id}`, body);
      if (res.status >= 400) throwBody('objective-action', res);
      return res.status === 204 ? null : (res.data as ObjectiveDTO);
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}

// ─── /api/me/evaluation (Espace employé) ─────────────────────
export function useMyEvaluation(year: number, enabled = true) {
  // 404 = signed-in account has no employee fiche — normal for staff
  // without a matching email; surfaced as `null` like useMeOverview.
  return useQuery<MyEvaluationDTO | null>({
    queryKey: ['me-evaluation', year],
    queryFn: async () => {
      const res = await apiClient.get('/me/evaluation', { params: { year } });
      if (res.status === 404) return null;
      if (res.status >= 400) throw new Error(`/me/evaluation failed: ${res.status}`);
      return res.data as MyEvaluationDTO;
    },
    enabled,
    staleTime: 15_000,
  });
}
export function useSaveMyAutoEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; notes: EvalNotes; bilan: string[] }) => {
      const res = await apiClient.put('/me/evaluation/auto', args);
      if (res.status >= 400) throwBody('auto-save', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useSubmitMyAutoEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number }) => {
      const res = await apiClient.post('/me/evaluation/auto/submit', args);
      if (res.status >= 400) throwBody('auto-submit', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useChooseMyOtoSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; d: string; h: string }) => {
      const res = await apiClient.post('/me/evaluation/oto/choose', args);
      if (res.status >= 400) throwBody('oto-choose', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useSignMyEval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number }) => {
      const res = await apiClient.post('/me/evaluation/sign', args);
      if (res.status >= 400) throwBody('sign', res);
      return res.data as EvaluationDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useProposeMyObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { year: number; cat: string; title: string; description?: string; kpi: string; deadline: string }) => {
      const res = await apiClient.post('/me/evaluation/objectives', args);
      if (res.status >= 400) throwBody('objective-propose', res);
      return res.data as ObjectiveDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}
export function useCheckMyObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; q: EvalQuarter; prog: number; status: ObjectiveCheckStatus; note?: string }) => {
      const { id, ...body } = args;
      const res = await apiClient.post(`/me/evaluation/objectives/${id}/check`, body);
      if (res.status >= 400) throwBody('objective-check', res);
      return res.data as ObjectiveDTO;
    },
    onSuccess: () => invalidateEvaluations(qc),
  });
}

// ─── /api/org (Organigramme) ─────────────────────────────────
// One node per employee still on the roster; `role` is the fiche's
// « Fonction », `managerId` the reporting link (null = top of the
// chart, or not attached yet).
export interface OrgNodeDTO {
  id: string;
  code: string;
  name: string;
  entityId: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  managerId: string | null;
  hiredAt: string | null;
}
export function useOrg() {
  return useQuery<{ nodes: OrgNodeDTO[] }>({
    queryKey: ['org'],
    queryFn: async () => {
      const res = await apiClient.get('/org');
      if (res.status >= 400) throw new Error(`/org failed: ${res.status}`);
      return res.data as { nodes: OrgNodeDTO[] };
    },
    staleTime: 30_000,
  });
}
export function useSetManager() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; managerId: string | null }) => {
      const res = await apiClient.patch(`/org/${args.id}/manager`, { managerId: args.managerId });
      if (res.status >= 400) throwBody('org-manager', res);
      return res.data as { ok: true; id: string; managerId: string | null };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org'] }),
  });
}

// ─── /api/recrutement (phase 1: catalogue + demandes) ────────
export interface RecTplReadyDTO { key: string; ready: boolean }
export interface RecRoleTemplates {
  presel?: Array<{ id: string; lbl: string }>;
  script?: Array<{ id: string; q: string; ph?: string; flag?: boolean }>;
  grid?: Array<{ id: string; lbl: string; co: number }>;
  kits?: Record<string, string[]>;
  [k: string]: unknown;
}
export interface RecRoleDTO {
  id: string; label: string; dept: string; active: boolean;
  // null quand le champ salaire est restreint (recrutement.salaires).
  bands: Record<string, [number, number]> | null;
  templates: RecRoleTemplates;
  tplReady: RecTplReadyDTO[];
  openCount: number;
}
export type RecApprovalStatus = 'en_attente' | 'courante' | 'validee' | 'refusee';
export interface RecApprovalDTO {
  round: number; step: number; roleLabel: string; status: RecApprovalStatus;
  decider: string | null; decidedAt: string | null; comment: string | null;
}
export type RecReqStatus = 'brouillon' | 'en_attente' | 'validee' | 'ouverte' | 'pourvue' | 'gelee' | 'annulee';
export interface RecRequisitionDTO {
  id: string; roleId: string; roleLabel: string; dept: string; entityId: string;
  seniority: string; contractType: 'CDI' | 'CDD'; cddMonths: number | null;
  motif: string; replacedName: string | null; targetStart: string;
  justification: string; status: RecReqStatus; refusalComment: string | null;
  outcomes90: string[]; outcomes12m: string[];
  band: [number, number] | null;
  hiringManager: { id: string; name: string } | null;
  createdBy: string; createdAt: string; openedAt: string | null;
  approvals: RecApprovalDTO[];
}
export interface RecQueueReqItemDTO {
  kind: 'requisition';
  requisition: RecRequisitionDTO;
  current: RecApprovalDTO | null;
  canDecide: boolean;
}
// Offre en attente dans la file — porte tout le contexte de décision
// (jury agrégé, montant vs fourchette, diff vs version précédente).
export interface RecQueueOfferItemDTO {
  kind: 'offer';
  offer: RecOfferDTO;
  prev: RecOfferDTO | null;
  applicationId: string;
  candidate: { firstName: string; lastName: string; avColor: string };
  roleLabel: string;
  entityId: string;
  contrat: string;
  band: [number, number] | null;
  juryAvg: number | null;
  juryVerdicts: Array<{ author: string; verdict: 'oui' | 'reserve' | 'non' }>;
  current: RecApprovalDTO | null;
  canDecide: boolean;
}
export type RecQueueItemDTO = RecQueueReqItemDTO | RecQueueOfferItemDTO;
export interface RecActivityDTO {
  at: string; by: string; action: string; payload: Record<string, unknown> | null;
}

export function useRecRoles() {
  return useQuery<RecRoleDTO[]>({
    queryKey: ['rec-roles'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/roles');
      if (res.status >= 400) throw new Error(`rec-roles failed: ${res.status}`);
      return (res.data as { roles: RecRoleDTO[] }).roles;
    },
    staleTime: 60_000,
  });
}
export function useRecRequisitions(entityId: string) {
  return useQuery<RecRequisitionDTO[]>({
    queryKey: ['rec-requisitions', entityId],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/requisitions', { params: { entityId } });
      if (res.status >= 400) throw new Error(`rec-requisitions failed: ${res.status}`);
      return (res.data as { requisitions: RecRequisitionDTO[] }).requisitions;
    },
    staleTime: 30_000,
  });
}
export function useRecQueue(enabled: boolean) {
  return useQuery<RecQueueItemDTO[]>({
    queryKey: ['rec-queue'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/queue');
      if (res.status >= 400) throw new Error(`rec-queue failed: ${res.status}`);
      return (res.data as { items: RecQueueItemDTO[] }).items;
    },
    enabled,
    staleTime: 15_000,
  });
}
export function useRecActivity() {
  return useQuery<RecActivityDTO[]>({
    queryKey: ['rec-activity'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/activity');
      if (res.status >= 400) throw new Error(`rec-activity failed: ${res.status}`);
      return (res.data as { items: RecActivityDTO[] }).items;
    },
    staleTime: 30_000,
  });
}

function invalidateRec(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['rec-requisitions'] });
  qc.invalidateQueries({ queryKey: ['rec-queue'] });
  qc.invalidateQueries({ queryKey: ['rec-roles'] });
  qc.invalidateQueries({ queryKey: ['rec-activity'] });
}

export interface RecCreateReqInput {
  roleId: string; entityId: string; seniority: string;
  contractType: 'CDI' | 'CDD'; cddMonths?: number;
  motif: string; replacedName?: string;
  targetStart: string; justification: string;
  outcomes90: string[]; outcomes12m: string[];
}
export function useCreateRecRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: RecCreateReqInput) => {
      const res = await apiClient.post('/recrutement/requisitions', args);
      if (res.status >= 400) throwBody('rec-create', res);
      return (res.data as { requisition: RecRequisitionDTO }).requisition;
    },
    onSuccess: () => invalidateRec(qc),
  });
}
export function useDecideRecRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; ok: boolean; comment?: string }) => {
      const res = await apiClient.post(`/recrutement/requisitions/${args.id}/decide`, { ok: args.ok, comment: args.comment });
      if (res.status >= 400) throwBody('rec-decide', res);
      return (res.data as { requisition: RecRequisitionDTO }).requisition;
    },
    onSuccess: () => invalidateRec(qc),
  });
}
export function useResubmitRecRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; justification?: string }) => {
      const res = await apiClient.post(`/recrutement/requisitions/${args.id}/resubmit`, { justification: args.justification });
      if (res.status >= 400) throwBody('rec-resubmit', res);
      return (res.data as { requisition: RecRequisitionDTO }).requisition;
    },
    onSuccess: () => invalidateRec(qc),
  });
}
export function useRecReqStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: 'OUVERTE' | 'GELEE' | 'ANNULEE' | 'POURVUE' }) => {
      const res = await apiClient.patch(`/recrutement/requisitions/${args.id}`, { status: args.status });
      if (res.status >= 400) throwBody('rec-status', res);
      return (res.data as { requisition: RecRequisitionDTO }).requisition;
    },
    onSuccess: () => invalidateRec(qc),
  });
}

/** L'owner désigne le manager recruteur d'un poste (Paramètres → Managers recruteurs). */
export function useRecHiringManager() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; userId: string }) => {
      const res = await apiClient.patch(`/recrutement/requisitions/${args.id}/hiring-manager`, { userId: args.userId });
      if (res.status >= 400) throwBody('rec-hiring-manager', res);
      return (res.data as { requisition: RecRequisitionDTO }).requisition;
    },
    onSuccess: () => invalidateRec(qc),
  });
}

// ─── Recrutement v2 — éditeur de catalogue ───────────────────

export type RecBands = Record<string, [number, number]>;
export interface RecRoleCreateInput { label: string; dept: string; bands?: RecBands }
export interface RecRolePatchInput { id: string; label?: string; dept?: string; active?: boolean; bands?: RecBands }

function invalidateRecRoles(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['rec-roles'] });
  qc.invalidateQueries({ queryKey: ['rec-activity'] });
}

export function useRecRoleCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: RecRoleCreateInput) => {
      const res = await apiClient.post('/recrutement/roles', args);
      if (res.status >= 400) throwBody('rec-role-create', res);
      return (res.data as { role: RecRoleDTO }).role;
    },
    onSuccess: () => invalidateRecRoles(qc),
  });
}
export function useRecRolePatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: RecRolePatchInput) => {
      const res = await apiClient.patch(`/recrutement/roles/${id}`, body);
      if (res.status >= 400) throwBody('rec-role-patch', res);
      return (res.data as { role: RecRoleDTO }).role;
    },
    onSuccess: () => invalidateRecRoles(qc),
  });
}
export function useRecRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; key: string; value: unknown }) => {
      const res = await apiClient.patch(`/recrutement/roles/${args.id}/template/${args.key}`, { value: args.value });
      if (res.status >= 400) throwBody('rec-role-template', res);
      return (res.data as { role: RecRoleDTO }).role;
    },
    onSuccess: () => invalidateRecRoles(qc),
  });
}
export function useRecRoleTemplateClear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; key: string }) => {
      const res = await apiClient.delete(`/recrutement/roles/${args.id}/template/${args.key}`);
      if (res.status >= 400) throwBody('rec-role-template-clear', res);
      return (res.data as { role: RecRoleDTO }).role;
    },
    onSuccess: () => invalidateRecRoles(qc),
  });
}

// ─── Recrutement phase 2 — pipeline candidats ────────────────

export type RecStageId =
  | 'NOUVELLE' | 'PRESEL' | 'APPEL' | 'TEST' | 'ENTRETIEN' | 'FINALISTE'
  | 'OFFRE' | 'RECRUTE' | 'REJETE' | 'VIVIER';

export interface RecCandidateDTO {
  id: string; firstName: string; lastName: string;
  email: string; phone: string | null; city: string | null;
  linkedin: string | null; expSummary: string | null;
  tags: string[]; avColor: string;
  cvUrl: string | null; portfolioUrl: string | null; letterUrl: string | null;
  consentAt: string;
  // Pièces téléversées — métadonnées ; le contenu passe par recFileUrl.
  files: RecCandidateFileDTO[];
}
export type RecFileKind = 'cv' | 'portfolio' | 'lettre' | 'autre';
export interface RecCandidateFileDTO {
  id: string; kind: RecFileKind; filename: string; mime: string; size: number; by: string | null; at: string;
}
export interface RecPrevApplicationDTO {
  applicationId: string; roleLabel: string; entityId: string;
  year: number; stage: RecStageId; rejectMotif: string | null;
}
export type RecDispoDelay = 'now' | '1m' | '2m' | '3m';
export interface RecCallDTO {
  scheduledAt: string | null; completedAt: string | null;
  callerId: string | null; caller: string | null;
  answers: Record<string, string>;
  verdict: 'go' | 'vivier' | 'stop' | null;
  // v2 — réponses structurées (montant masqué sans recrutement.salaires).
  pretAmount: number | null; pretType: 'net' | 'brut' | null; pretSet: boolean; pretInBand: boolean | null;
  dispoDate: string | null; dispoDelay: RecDispoDelay | null; commune: string | null; vehicule: boolean | null; trajetMin: number | null;
  chkTrajet: boolean | null; chkPoste: boolean | null;
  // Signalé « appel terminé » par l'appelant, en attente du verdict de l'owner.
  reportedAt: string | null; reportedBy: string | null;
}
// v2 — cas pratique rémunéré.
export interface RecTestDTO {
  status: 'A_ENVOYER' | 'ENVOYE' | 'RENDU' | 'NOTE';
  brief: string; deliverables: string[]; rubric: RecGridCrit[];
  delayH: number; fee: number | null;
  sentAt: string | null; dueAt: string | null; submittedAt: string | null; link: string | null; paidAt: string | null;
  scores: Record<string, number>; evidence: Record<string, string>;
  scorer: string | null; scoredAt: string | null; score: number | null;
}
// v2 — vérification avant l'offre.
export interface RecVerificationDTO {
  cert: boolean | null; certNote: string | null; dipl: boolean | null; port: boolean | null; portNote: string | null;
  refs: RecRef[]; integrity: 'OK' | 'PROBLEME' | null;
}
// v2 — portes de l'offre (masquées pour un juré qui n'a pas soumis).
export interface RecGatesDTO {
  masked: boolean;
  jury: RecDimScore | null; bar: RecBar | null; overridden: boolean;
  test: { score: number | null; pass: boolean };
  verif: { refsOk: boolean; mgr: number; n: number; cert: boolean; integ: boolean; problem: boolean; ok: boolean; done: number };
  veto: boolean; mgrVerdict: string | null; mgrInJury: boolean;
  open: boolean; closed: Array<'bar' | 'test' | 'verif' | 'veto'>;
}
export interface RecScorecardDTO {
  id: string; authorId: string; author: string;
  scores: Record<string, number>;
  evidence: Record<string, string>;
  verdict: 'oui' | 'reserve' | 'non';
  comment: string | null; at: string;
}
export type RecItwStatusId = 'PLANIFIE' | 'REALISE' | 'ANNULE' | 'NO_SHOW';
export interface RecInterviewDTO {
  id: string; type: string; scheduledAt: string; duration: number;
  mode: string; location: string | null; status: RecItwStatusId;
  kit: string[];
  interviewers: Array<{ id: string; name: string }>;
  amJuror: boolean;
  // Révélation séquentielle: `scorecards` est null pour un juré qui n'a
  // pas soumis la sienne — le serveur n'envoie jamais le contenu.
  myScorecard: RecScorecardDTO | null;
  scorecards: RecScorecardDTO[] | null;
  scorecardCount: number;
}
export interface RecApplicationDTO {
  id: string; requisitionId: string;
  stage: RecStageId; stageSince: string;
  stageHistory: Array<{ stage: string; at: string; by: string; how: string }>;
  source: string; referredBy: string | null; waitOn: 'rh' | 'mgr' | null;
  presel: Record<string, boolean>;
  rejectMotif: string | null; rejectNote: string | null;
  closedAt: string | null; closedBy: string | null;
  candidate: RecCandidateDTO;
  prevApplications: RecPrevApplicationDTO[];
  notes: Array<{ id: string; text: string; by: string; at: string }>;
  emails: Array<{ id: string; templateId: string; subject: string; status: string; at: string }>;
  call: RecCallDTO | null;
  interviews: RecInterviewDTO[];
  offers: RecOfferDTO[];
  test: RecTestDTO | null;
  verification: RecVerificationDTO | null;
  barOverride: { by: string; comment: string; at: string; fails: string[] } | null;
  negReply: 'open' | 'sent' | 'dismissed' | null;
  gates: RecGatesDTO;
}
export interface RecCandidateCheckDTO {
  id: string; firstName: string; lastName: string; city: string | null;
  applications: Array<{ requisitionId: string; roleLabel: string; entityId: string; year: number; stage: RecStageId; rejectMotif: string | null }>;
}

export function useRecPipeline(enabled = true) {
  return useQuery<RecApplicationDTO[]>({
    queryKey: ['rec-pipeline'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/pipeline');
      if (res.status >= 400) throw new Error(`rec-pipeline failed: ${res.status}`);
      return (res.data as { applications: RecApplicationDTO[] }).applications;
    },
    enabled,
    staleTime: 15_000,
  });
}

/** Dédup à la saisie — appelé (debounced) par la modal candidat. */
export async function checkRecCandidate(email: string, requisitionId?: string): Promise<RecCandidateCheckDTO | null> {
  // requisitionId : un manager recruteur ne voit que l'historique sur ce poste.
  const res = await apiClient.get('/recrutement/candidates/check', { params: { email, ...(requisitionId ? { requisitionId } : {}) } });
  if (res.status >= 400) return null;
  return (res.data as { candidate: RecCandidateCheckDTO | null }).candidate;
}

// Chaque mutation renvoie la candidature à jour: on patch le cache du
// pipeline en place (snappy), et on invalide l'activité.
function patchPipeline(qc: ReturnType<typeof useQueryClient>, app: RecApplicationDTO) {
  qc.setQueryData<RecApplicationDTO[]>(['rec-pipeline'], (old) => {
    if (!old) return old;
    const i = old.findIndex((a) => a.id === app.id);
    // Une nouvelle candidature va en tête, comme le serveur (plus récentes d'abord).
    return i === -1 ? [app, ...old] : old.map((a) => (a.id === app.id ? app : a));
  });
  qc.invalidateQueries({ queryKey: ['rec-activity'] });
}

export interface RecCreateCandidateInput {
  requisitionId: string;
  firstName: string; lastName: string; email: string;
  phone?: string; city?: string; linkedin?: string; expSummary?: string;
  tags: string[]; cvUrl?: string; portfolioUrl?: string; letterUrl?: string;
  source: string; referredBy?: string;
}
export function useCreateRecCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: RecCreateCandidateInput) => {
      const res = await apiClient.post('/recrutement/candidates', args);
      if (res.status >= 400) throwBody('rec-candidate', res);
      return (res.data as { application: RecApplicationDTO }).application;
    },
    onSuccess: (app) => patchPipeline(qc, app),
  });
}

function appMutation(path: (id: string) => string) {
  return function useAppMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (args: { id: string } & Record<string, unknown>) => {
        const { id, ...body } = args;
        const res = await apiClient.post(path(id), body);
        if (res.status >= 400) throwBody('rec-app', res);
        return (res.data as { application: RecApplicationDTO }).application;
      },
      onSuccess: (app) => patchPipeline(qc, app),
    });
  };
}
export const useRecAppStage = appMutation((id) => `/recrutement/applications/${id}/stage`);
export const useRecAppPresel = appMutation((id) => `/recrutement/applications/${id}/presel`);
export const useRecAppPreselValidate = appMutation((id) => `/recrutement/applications/${id}/presel/validate`);
export const useRecAppReject = appMutation((id) => `/recrutement/applications/${id}/reject`);
export const useRecAppVivier = appMutation((id) => `/recrutement/applications/${id}/vivier`);
export const useRecAppRestore = appMutation((id) => `/recrutement/applications/${id}/restore`);
export const useRecAppNote = appMutation((id) => `/recrutement/applications/${id}/notes`);
export const useRecAppEmail = appMutation((id) => `/recrutement/applications/${id}/email`);

// ─── Recrutement v2 — cas pratique, vérification, dérogation ─
export const useRecTestPatch = appMutation((id) => `/recrutement/applications/${id}/test`);
export const useRecTestSend = appMutation((id) => `/recrutement/applications/${id}/test/send`);
export const useRecTestSubmit = appMutation((id) => `/recrutement/applications/${id}/test/submit`);
export const useRecTestPay = appMutation((id) => `/recrutement/applications/${id}/test/pay`);
export const useRecTestScore = appMutation((id) => `/recrutement/applications/${id}/test/score`);
export const useRecTestAdvance = appMutation((id) => `/recrutement/applications/${id}/test/advance`);
export const useRecVerifRef = appMutation((id) => `/recrutement/applications/${id}/verification/refs`);
export const useRecBarOverride = appMutation((id) => `/recrutement/applications/${id}/bar-override`);
export const useRecNegDismiss = appMutation((id) => `/recrutement/applications/${id}/neg-reply/dismiss`);
export function useRecVerifPatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string } & Record<string, unknown>) => {
      const { id, ...body } = args;
      const res = await apiClient.patch(`/recrutement/applications/${id}/verification`, body);
      if (res.status >= 400) throwBody('rec-verif', res);
      return (res.data as { application: RecApplicationDTO }).application;
    },
    onSuccess: (app) => patchPipeline(qc, app),
  });
}
export function useRecSettings(enabled = true) {
  return useQuery<RecSettings>({
    queryKey: ['rec-settings'],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/settings');
      if (res.status >= 400) throw new Error(`rec-settings failed: ${res.status}`);
      return (res.data as { settings: RecSettings }).settings;
    },
    staleTime: 5 * 60_000,
  });
}
export function useRecSettingsSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RecSettings) => {
      const res = await apiClient.put('/recrutement/settings', body);
      if (res.status >= 400) throwBody('rec-settings', res);
      return (res.data as { settings: RecSettings }).settings;
    },
    onSuccess: (st) => {
      qc.setQueryData(['rec-settings'], st);
      qc.invalidateQueries({ queryKey: ['rec-pipeline'] });
    },
  });
}

// ─── Recrutement phase 3 — évaluation ────────────────────────

export interface RecInterviewerDTO { id: string; name: string; role: string }
export function useRecInterviewers(enabled: boolean) {
  return useQuery<RecInterviewerDTO[]>({
    queryKey: ['rec-interviewers'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/interviewers');
      if (res.status >= 400) throw new Error(`rec-interviewers failed: ${res.status}`);
      return (res.data as { interviewers: RecInterviewerDTO[] }).interviewers;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export const useRecCallPlan = appMutation((id) => `/recrutement/applications/${id}/call`);
export const useRecCallSave = appMutation((id) => `/recrutement/applications/${id}/call/save`);
export const useRecCallClose = appMutation((id) => `/recrutement/applications/${id}/call/close`);
export const useRecCallReport = appMutation((id) => `/recrutement/applications/${id}/call/report`);
export const useRecItwCreate = appMutation((id) => `/recrutement/applications/${id}/interviews`);
// Scorecard: POST sur l'entretien — même retour (candidature à jour).
export const useRecScorecard = appMutation((id) => `/recrutement/interviews/${id}/scorecard`);

export function useRecItwPatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string } & Record<string, unknown>) => {
      const { id, ...body } = args;
      const res = await apiClient.patch(`/recrutement/interviews/${id}`, body);
      if (res.status >= 400) throwBody('rec-itw', res);
      return (res.data as { application: RecApplicationDTO }).application;
    },
    onSuccess: (app) => patchPipeline(qc, app),
  });
}

// ─── Recrutement phase 4 — offres ────────────────────────────

export type RecOfferStatusId =
  | 'BROUILLON' | 'EN_ATTENTE' | 'VALIDEE' | 'ENVOYEE'
  | 'ACCEPTEE' | 'REFUSEE' | 'CONTRE';
export interface RecOfferDTO {
  id: string; version: number; status: RecOfferStatusId;
  // null quand le champ salaire est restreint (recrutement.salaires).
  salaire: number | null; primes: string | null;
  avantages: string[]; essai: string; startDate: string; deadlineDays: number;
  refusalComment: string | null;
  sentAt: string | null; responseDeadline: string | null;
  relances: string[];
  response: 'acceptee' | 'refusee' | 'contre' | null;
  respondedAt: string | null;
  counterDetails: string | null;
  createdBy: string; createdAt: string;
  approvals: RecApprovalDTO[];
}
export interface RecOfferInput {
  salaire: number; primes?: string; avantages: string[];
  essai: string; startDate: string; deadlineDays: number;
}

// Les mutations d'offre touchent aussi la file de validation — patch du
// pipeline + invalidation de la queue.
function offerMutation(method: 'post' | 'patch', path: (id: string) => string) {
  return function useOfferMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (args: { id: string } & Record<string, unknown>) => {
        const { id, ...body } = args;
        const res = method === 'post' ? await apiClient.post(path(id), body) : await apiClient.patch(path(id), body);
        if (res.status >= 400) throwBody('rec-offer', res);
        return (res.data as { application: RecApplicationDTO }).application;
      },
      onSuccess: (app) => {
        patchPipeline(qc, app);
        qc.invalidateQueries({ queryKey: ['rec-queue'] });
        // Une acceptation crée l'onboarding (phase 5).
        qc.invalidateQueries({ queryKey: ['rec-onboarding'] });
      },
    });
  };
}
// id = candidature pour la création, id = offre pour le reste.
export const useRecOfferCreate = offerMutation('post', (id) => `/recrutement/applications/${id}/offer`);
export const useRecOfferPatch = offerMutation('patch', (id) => `/recrutement/offers/${id}`);
export const useRecOfferSubmit = offerMutation('post', (id) => `/recrutement/offers/${id}/submit`);
export const useRecOfferDecide = offerMutation('post', (id) => `/recrutement/offers/${id}/decide`);
export const useRecOfferSend = offerMutation('post', (id) => `/recrutement/offers/${id}/send`);
export const useRecOfferRemind = offerMutation('post', (id) => `/recrutement/offers/${id}/remind`);
export const useRecOfferResponse = offerMutation('post', (id) => `/recrutement/offers/${id}/response`);

/** URL du PDF d'une version d'offre (téléchargement — champ restreint). */
export function recFileUrl(appId: string, fileId: string): string {
  return `/api/recrutement/applications/${appId}/files/${fileId}`;
}
/** Pièce du candidat : multipart (8 Mo max — PDF, Word, PNG, JPEG). */
export function useRecFileUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { appId: string; file: File; kind: RecFileKind }) => {
      const form = new FormData();
      form.append('kind', args.kind);
      form.append('file', args.file);
      const res = await apiClient.post(`/recrutement/applications/${args.appId}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 400) throwBody('rec-file', res);
      return (res.data as { application: RecApplicationDTO }).application;
    },
    onSuccess: (app) => patchPipeline(qc, app),
  });
}
export function useRecFileDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { appId: string; fileId: string }) => {
      const res = await apiClient.delete(`/recrutement/applications/${args.appId}/files/${args.fileId}`);
      if (res.status >= 400) throwBody('rec-file', res);
      return (res.data as { application: RecApplicationDTO }).application;
    },
    onSuccess: (app) => patchPipeline(qc, app),
  });
}

export function recOfferPdfUrl(offerId: string): string {
  return `/api/recrutement/offers/${offerId}/pdf`;
}

// ─── Recrutement phase 5 — onboarding ────────────────────────

export interface RecObDocDTO { id: string; nm: string; st: 'manq' | 'recu' | 'ok'; by?: string; at?: string }
export interface RecObTaskDTO { id: string; tx: string; ow: string; done: boolean; doneBy?: string; doneAt?: string }
export interface RecObResourceDTO { nm: string; when: string; ic?: string; shared: boolean; sharedAt?: string; sharedBy?: string }
export interface RecOnboardingDTO {
  id: string;
  applicationId: string;
  start: string;
  buddy: { id: string; name: string } | null;
  dossier: RecObDocDTO[];
  plan: Record<string, RecObTaskDTO[]>;
  resources: RecObResourceDTO[];
  oneOnOne: Array<{ when: string; agenda: string }>;
  employee: { id: string; code: string; name: string } | null;
  handedAt: string | null;
  candidate: { firstName: string; lastName: string; avColor: string; email: string; phone: string | null };
  roleLabel: string;
  entityId: string;
  contrat: string;
  manager: string | null;
  hiringManagerId: string | null;
  createdAt: string;
  // v2 — période d'essai.
  probationMonths: number;
  probationRenewable: boolean;
  outcomes: Array<{ t: string; st: string }>;
  hireScores: Record<string, number>;
  grid: RecGridCrit[];
  checkpoints: RecCheckpointDTO[];
}
export interface RecCheckpointDTO {
  key: string; day: number; type: 'alert' | 'review' | 'pulse' | 'final'; lbl: string; dueDate: string;
  done: boolean; doneAt?: string; doneBy?: string;
  data: {
    v?: string; change?: string; scores?: Record<string, number>; rehire?: string; plan?: Array<{ t: string; d: string }>;
    drops?: string[]; answers?: Array<{ who: string; a: string[] }>;
    signs?: Record<string, { by: string; byId: string; v: string; comment: string; at: string; dual?: boolean }>; decision?: string;
  };
}

export function useRecOnboarding(enabled = true) {
  return useQuery<RecOnboardingDTO[]>({
    queryKey: ['rec-onboarding'],
    queryFn: async () => {
      const res = await apiClient.get('/recrutement/onboarding');
      if (res.status >= 400) throw new Error(`rec-onboarding failed: ${res.status}`);
      return (res.data as { onboardings: RecOnboardingDTO[] }).onboardings;
    },
    enabled,
    staleTime: 15_000,
  });
}

// Chaque mutation renvoie l'onboarding à jour — patch du cache en place.
function obMutation(path: (id: string) => string, alsoInvalidate?: string[]) {
  return function useObMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (args: { id: string } & Record<string, unknown>) => {
        const { id, ...body } = args;
        const res = await apiClient.post(path(id), body);
        if (res.status >= 400) throwBody('rec-onboarding', res);
        return (res.data as { onboarding: RecOnboardingDTO }).onboarding;
      },
      onSuccess: (ob) => {
        qc.setQueryData<RecOnboardingDTO[]>(['rec-onboarding'], (old) =>
          old ? old.map((x) => (x.id === ob.id ? ob : x)) : old);
        qc.invalidateQueries({ queryKey: ['rec-activity'] });
        for (const key of alsoInvalidate ?? []) qc.invalidateQueries({ queryKey: [key] });
      },
    });
  };
}
export const useRecObTask = obMutation((id) => `/recrutement/onboarding/${id}/task`);
export const useRecObDoc = obMutation((id) => `/recrutement/onboarding/${id}/doc`);
export const useRecObResource = obMutation((id) => `/recrutement/onboarding/${id}/resource`);
export const useRecObBuddy = obMutation((id) => `/recrutement/onboarding/${id}/buddy`);
export const useRecObRemindDocs = obMutation((id) => `/recrutement/onboarding/${id}/remind-docs`);
// v2 — période d'essai : id = `${onboardingId}/checkpoints/${key}`.
export const useRecObCheckpoint = obMutation((id) => `/recrutement/onboarding/${id}`, ['employees']);
export const useRecObOutcome = obMutation((id) => `/recrutement/onboarding/${id}/outcomes`);
// Le handoff crée l'Employee et pourvoit la demande — on invalide large.
export const useRecObHandoff = obMutation(
  (id) => `/recrutement/onboarding/${id}/handoff`,
  ['rec-requisitions', 'rec-pipeline', 'employees'],
);

// ─── /api/usage (Connexions & usage — Paramètres, BOSS) ──────
// Read-only screens behind `usage.view`. The heartbeat that feeds
// them lives in lib/usageHeartbeat.ts, not here: it is fire-and-
// forget and must never go through React Query's retry/cache path.

export type UsageScope = 'staff' | 'employee' | 'all';

export interface UsageUserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  accountActive: boolean;
  sessions: number;
  activeSeconds: number;
  requestCount: number;
  avgSessionSeconds: number;
  longestSessionSeconds: number;
  daysActive: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  topArea: UsageArea | null;
  areas: Record<string, number>;
  devices: string[];
  /** activeSeconds per day, aligned with range.days. */
  daily: number[];
  online: boolean;
  currentArea: UsageArea | null;
}

export interface UsageOverviewDTO {
  generatedAt: string;
  scope: UsageScope;
  range: { from: string; to: string; days: string[] };
  truncated: boolean;
  totals: {
    accounts: number;
    activeUsers: number;
    dormantUsers: number;
    sessions: number;
    activeSeconds: number;
    avgSessionSeconds: number;
    avgPerUserSeconds: number;
    onlineNow: number;
  };
  daily: Array<{ date: string; activeSeconds: number; sessions: number; users: number }>;
  areas: Array<{ area: UsageArea; activeSeconds: number; share: number }>;
  online: Array<{
    sessionId: string;
    userId: string;
    name: string;
    role: Role;
    since: string;
    lastSeenAt: string;
    activeSeconds: number;
    area: UsageArea | null;
    device: string | null;
  }>;
  users: UsageUserRow[];
}

export interface UsageSessionDTO {
  id: string;
  user: { id: string; name: string; email: string; role: Role } | null;
  method: string;
  startedAt: string;
  lastSeenAt: string;
  endedAt: string | null;
  endReason: string | null;
  open: boolean;
  spanSeconds: number;
  activeSeconds: number;
  requestCount: number;
  topArea: UsageArea | null;
  areas: Record<string, number>;
  ip: string | null;
  device: string | null;
  userAgent: string | null;
}

export interface UsageSessionsPage {
  total: number;
  limit: number;
  offset: number;
  entries: UsageSessionDTO[];
}

export interface UsageRangeFilters {
  from: string;
  to: string;
  scope: UsageScope;
}

export function useUsageOverview(filters: UsageRangeFilters, enabled = true) {
  return useQuery<UsageOverviewDTO>({
    queryKey: ['usage-overview', filters],
    queryFn: async () => {
      const res = await apiClient.get('/usage/overview', { params: filters });
      if (res.status >= 400) throw new Error(`/usage/overview failed: ${res.status}`);
      return res.data as UsageOverviewDTO;
    },
    enabled,
    // The live strip is the reason this is short: a boss watching the
    // screen expects "en ligne maintenant" to actually mean now.
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useUsageSessions(
  filters: UsageRangeFilters & { userId?: string; limit?: number },
  enabled = true,
) {
  return useQuery<UsageSessionsPage>({
    queryKey: ['usage-sessions', filters],
    queryFn: async () => {
      const res = await apiClient.get('/usage/sessions', { params: filters });
      if (res.status >= 400) throw new Error(`/usage/sessions failed: ${res.status}`);
      return res.data as UsageSessionsPage;
    },
    enabled,
    staleTime: 30_000,
  });
}

// ─── /api/notifications ──────────────────────────────────────
// Le compteur est le seul appel périodique (60 s, onglet au premier
// plan uniquement) ; la liste ne part qu'à l'ouverture du panneau.

export function useUnreadNotifCount() {
  return useQuery<number>({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      const res = await apiClient.get('/notifications/unread-count');
      if (res.status >= 400) throw new Error(`/notifications/unread-count failed: ${res.status}`);
      return (res.data as { count: number }).count;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useNotifications(enabled: boolean) {
  return useInfiniteQuery<NotificationListResponse>({
    queryKey: ['notifications', 'list'],
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get('/notifications', {
        params: pageParam ? { cursor: pageParam } : undefined,
      });
      if (res.status >= 400) throw new Error(`/notifications failed: ${res.status}`);
      return res.data as NotificationListResponse;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 30_000,
  });
}

export function useMarkNotifsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { ids?: string[]; all?: boolean }) => {
      const res = await apiClient.post('/notifications/read', body);
      if (res.status >= 400) throw new Error(`POST /notifications/read failed: ${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ─── Web Push (phase 2) ──────────────────────────────────────
// 404 = push non configuré côté serveur (pas de clés VAPID) : ce
// n'est pas une erreur, juste « désactivé » → data null.

export function useVapidKey() {
  return useQuery<string | null>({
    queryKey: ['notifications', 'vapid-key'],
    queryFn: async () => {
      const res = await apiClient.get('/notifications/vapid-key');
      if (res.status === 404) return null;
      if (res.status >= 400) throw new Error(`/notifications/vapid-key failed: ${res.status}`);
      return (res.data as { key: string }).key;
    },
    retry: false,
    staleTime: Infinity,
  });
}

// L'abonnement courant du navigateur, pour savoir quel état afficher.
export function usePushSubscription(enabled: boolean) {
  return useQuery<boolean>({
    queryKey: ['notifications', 'push-sub'],
    queryFn: async () => (await getSubscription()) !== null,
    enabled,
    staleTime: 30_000,
  });
}

export function useSubscribePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vapidKey: string) => subscribePush(vapidKey),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications', 'push-sub'] }),
  });
}

export function useUnsubscribePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unsubscribePush(),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications', 'push-sub'] }),
  });
}
